/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable max-nested-callbacks */
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
  Chip,
  Menu,
  MenuItem,
  CircularProgress,
  Skeleton,
  LinearProgress,
  Dialog,
  DialogContent,
  DialogActions,
  AccordionDetails,
  Accordion,
  Autocomplete,
  TextField,
  InputLabel,
  AccordionSummary,
  Radio,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { getDecryptAns } from '../../../../../utils/decryptAns';
import { nanoid } from 'nanoid';
import { toast } from 'react-toastify';
import { useProgramCreation } from '../../ProgramCreationContext';
import { API } from '../../../../../api';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProgramContext } from '../../../../../hooks/useProgramContext';
import useEditingFrozen from '../../../../../hooks/useEditingFrozen';
import { EDITING_FROZEN_MESSAGE } from '../../../../../utils/constants';
import { EditingFrozenBanner, ReadOnlyWhenFrozen } from '../../../../../components/program-freeze/EditingFrozenGuard';
import {
  CollaboratorBar,
  EditLockBanner,
  ProgramEditLockProvider,
  ReadOnlyWhenLocked,
  setBuildStateHydrating,
  useEditLock,
  useEditLockTarget
} from '../../../../program-collaboration';
import {
  Lesson,
  ModuleDraft,
  createLesson,
  createModule,
  LessonMaterial,
  FeedbackQuestion,
  FeedbackQuestionType,
  mapFeedbackTypeToAnswerFormat,
  mapAnswerFormatToFeedbackType,
  getFeedbackTypeLabel
} from '../types';
import ContentsModal from '../../../../../pages/new-designs/tabs/Program/components/program tabs/settings components/ContentsModal';
import CommonModalHeader from '../../../../../components/DialogUi/CommonModalHeader';
import ElliottSectionsPanel from './ElliottSectionsPanel';
import { mapTemplateToModules } from '../../../../../pages/new-designs/tabs/Program/components/program tabs/ProgramSettings/utils';
import type { ElliottSections } from '../../../../../pages/new-designs/tabs/aimd-v2/types';
import ImproveWithAIModal from '../../../../../pages/new-designs/tabs/aimd-v2/components/ImproveWithAIModal';
import AddEditContent from '../../../../../pages/new-designs/tabs/Help/components/AddEditContent';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { eventTracking, events, attributeKeys } from '../../../../../utils/amplitude';
import { trackProgramCreated } from '../../../../../utils/amplitudeProductTracking';
import { fetchAllArticles, IArticleData } from '../../../../../redux/slices/article/getAllArticleSlice';

type ArticleDetailsForMaterials = IArticleData & {
  title?: string;
  name?: string;
  coverImg?: string;
  thumbnail?: string;
  articleUrl?: string;
  articleLink?: string;
};
import { fetchGroupDetails, grpDetailsActions } from '../../../../../redux/slices/group-details/groupDetails';
import DosenInput from '../../../../../pages/new-designs/components/DosenInput';
import StepNavigator from '../components/StepNavigator';
import DosenButton from '../../../../../components/ui/Buttons/DosenButton';
import { Bone } from '../../../../../components/ui/DashboardSkeletons';
import { CTA_GRADIENT, ctaButtonSx, secondaryButtonSx } from '../../../../../components/ui/Buttons/buttonStyles';
import { GenericAccordion } from '../../../../../pages/new-designs/tabs/Program/components/program tabs/ProgramSettings';
import DosenAutocomplete from '../../../../../pages/new-designs/components/DosenAutocomplete';
import LessonDescriptionEditor, { LessonDescriptionEditorInsertRef } from '../../../../../pages/new-designs/tabs/Program/components/program tabs/settings components/LessonDescriptionEditor';
import DueDateModal, { ProgramDueDateData } from '../../../../../pages/new-designs/tabs/Program/components/program tabs/settings components/Dialog/DueDateModal';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import dosenFavicon from '../../../../../assets/images/brand/dosen/favicon.png';

// Shared "no articles loaded" array — see the note at its use site.
const EMPTY_ARTICLE_LIST: IArticleData[] = [];

const inputSx = {
  '& .MuiInputBase-root': {
    background: 'rgba(0,0,0,0.7) !important',
    borderRadius: '6px',
    color: '#FDFDFD'
  },
  '& .MuiOutlinedInput-notchedOutline': {
    background: 'rgba(0,0,0,0.7) !important',
    borderColor: '#868686',
    color: '#FDFDFD'
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    background: 'rgba(0,0,0,0.7) !important',
    borderColor: '#B4B4B4'
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    background: 'rgba(0,0,0,0.7) !important',
    borderColor: '#F79420'
  },
  '& .MuiInputLabel-root': {
    color: '#B6B6B6',
    background: 'rgba(0,0,0,0.7) !important'
  }
};

interface ManualProgramBuilderProps {
  showLessonEditor?: boolean;
  hideSaveButton?: boolean;
  registerSaveHandler?: (handler: (() => Promise<boolean>) | null) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onHasSavedBeforeNextChange?: (value: boolean) => void;
  skipTopicIndustryUpdate?: boolean;
  onProgramNameChange?: (value: string) => void;
  model?: string;
  useUpdateGroupTasks?: boolean;
  updateTemplateId?: string;
  // Hydrate state.groupId from the URL route param and fall back to the
  // BE group doc's `name` for the Program name field. Used only by the
  // Learning Framework Continue path on design-stage programs — the
  // ProgramSettings → DesignView path already has ManualBuilderInitializer
  // dispatching its own SET_GROUP_ID / UPDATE_MASTERCLASS / SET_MODULES,
  // and racing those would corrupt the template-driven module list.
  hydrateFromUrl?: boolean;
  // When embedded in the Add Program modal: called on finish (after publish)
  // instead of navigating to the Members sub-step, so the modal can close and
  // return to the framework. Members is skipped (audience captured in chat).
  onFinish?: () => void;
}

const ManualProgramBuilderInner = ({
  showLessonEditor = true,
  hideSaveButton = false,
  registerSaveHandler,
  onDirtyChange,
  onHasSavedBeforeNextChange,
  skipTopicIndustryUpdate = false,
  onProgramNameChange,
  model,
  useUpdateGroupTasks = false,
  updateTemplateId,
  hydrateFromUrl = false,
  onFinish
}: ManualProgramBuilderProps) => {
  const { state, dispatch } = useProgramCreation();
  // Relaunch editing freeze (B4-2): read-only builder, no writes leave the page.
  const editingFrozen = useEditingFrozen();
  const initialModules = (state.modules as ModuleDraft[] | undefined) ?? [];
  const [modules, setModules] = useState<ModuleDraft[]>(initialModules);
  const [programName, setProgramName] = useState(state.masterclass?.title || '');
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(modules[0]?.id || null);
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(modules[0]?.lessons[0]?.id || null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const hasSavedBeforeNext = Boolean(state.ai.hasSavedBeforeNext);
  const hydrationBlockedRef = useRef(false);
  const lastContentRef = useRef<any>(null);
  const handleSaveRef = useRef<() => Promise<boolean>>(() => Promise.resolve(false));
  const [draggingModule, setDraggingModule] = useState<string | null>(null);
  const [draggingLesson, setDraggingLesson] = useState<{ moduleId: string; lessonId: string } | null>(null);
  const [dragOverModuleId, setDragOverModuleId] = useState<string | null>(null);
  const [dragOverLessonMeta, setDragOverLessonMeta] = useState<{ moduleId: string; lessonId: string } | null>(null);
  const [moduleMenuAnchor, setModuleMenuAnchor] = useState<null | HTMLElement>(null);
  const [moduleMenuId, setModuleMenuId] = useState<string | null>(null);
  const [moduleDueDateTargetId, setModuleDueDateTargetId] = useState<string | null>(null);
  const [programDueDateOpen, setProgramDueDateOpen] = useState(false);
  const [programDueDate, setProgramDueDate] = useState<ProgramDueDateData | null>(null);
  const [lessonMenuAnchor, setLessonMenuAnchor] = useState<null | HTMLElement>(null);
  const [lessonMenuMeta, setLessonMenuMeta] = useState<{ moduleId: string; lessonId: string } | null>(null);
  const [editingModuleId, setEditingModuleId] = useState<string | null>(null);
  const [resourceChoiceLesson, setResourceChoiceLesson] = useState<{ moduleId: string; lessonId: string } | null>(null);
  const [articleDetailsCache, setArticleDetailsCache] = useState<Record<string, any>>({});
  const [isEditingLesson, setIsEditingLesson] = useState(false);
  const [lessonDraft, setLessonDraft] = useState<{ title: string; description: string }>({
    title: '',
    description: ''
  });
  const [feedbackExpanded, setFeedbackExpanded] = useState(true);
  const [mcqExpanded, setMcqExpanded] = useState(true);
  const [addFeedbackModalOpen, setAddFeedbackModalOpen] = useState(false);
  const [feedbackDraft, setFeedbackDraft] = useState<{ prompt: string; type: FeedbackQuestionType }>({
    prompt: '',
    type: 'text'
  });
  const [removeFeedbackId, setRemoveFeedbackId] = useState<string | null>(null);
  const [removeMcqId, setRemoveMcqId] = useState<string | null>(null);
  const [draggingOption, setDraggingOption] = useState<{ mcqId: string; optionId: string } | null>(null);
  // Quiz "Improve with AI" (Elliott) — lives on the Quiz Questions accordion header.
  const [quizImproveOpen, setQuizImproveOpen] = useState(false);
  const [quizImproveLoading, setQuizImproveLoading] = useState(false);
  const [addEditModalMeta, setAddEditModalMeta] = useState<{
    open: boolean;
    action: 'Add' | 'Edit';
    articleId?: string;
    moduleId?: string;
    lessonId?: string;
    materialId?: string;
    material?: LessonMaterial;
    articleData?: any;
  }>({ open: false, action: 'Add' });
  const [lessonRenameMeta, setLessonRenameMeta] = useState<{
    moduleId: string;
    lessonId: string;
    title: string;
  } | null>(null);
  const moduleListRef = useRef<HTMLDivElement | null>(null);
  const insertMediaRef = useRef<LessonDescriptionEditorInsertRef | null>(null);
  const [mediaDialog, setMediaDialog] = useState<{ type: 'image' | 'video'; open: boolean }>({ type: 'image', open: false });
  const [mediaPrompt, setMediaPrompt] = useState('');
  const [mediaGenerating, setMediaGenerating] = useState(false);
  const [imageCount, setImageCount] = useState(0);
  const [videoCount, setVideoCount] = useState(0);
  const [videoPollingJobs, setVideoPollingJobs] = useState<{ videoJobId: string; lessonId: string }[]>([]);
  const videoPollingRef = useRef<NodeJS.Timeout | null>(null);
  const { groupId: hookGroupId, orgId, coId, location } = useProgramContext();
  const routeGroupId = hookGroupId && hookGroupId !== 'admin' ? hookGroupId : coId;
  const setHasSavedBeforeNextState = useCallback(
    (value: boolean) => {
      onHasSavedBeforeNextChange?.(value);
      dispatch({ type: 'SET_AI_HAS_SAVED_BEFORE_NEXT', payload: value });
      const storageGroupId = state.groupId || routeGroupId;
      if (storageGroupId) {
        localStorage.setItem(`aiHasSavedBeforeNext:${storageGroupId}`, value ? 'true' : 'false');
      }
    },
    [dispatch, onHasSavedBeforeNextChange, routeGroupId, state.groupId]
  );
  const setDirtyState = useCallback(
    (value: boolean) => {
      setDirty(value);
      onDirtyChange?.(value);
      if (value) {
        setHasSavedBeforeNextState(false);
      }
    },
    [onDirtyChange, setHasSavedBeforeNextState]
  );
  const reduxDispatch = useAppDispatch();
  const refreshGroupDetails = useCallback(
    async (groupId: string) => {
      if (!groupId) return;
      await reduxDispatch(
        fetchGroupDetails({
          grpId: groupId,
          loc: location,
          useCache: false
        })
      );
    },
    [location, reduxDispatch]
  );
  const orgIdParamNew = useAppSelector((state) => state.orgDetails.data?._id) || '';
  // The BE's verifyOrganization middleware matches users.oid against the org's
  // Auth0 authId (org_…) — the Mongo _id NEVER matches and 400s any caller
  // whose JWT role isn't platform-admin. Elliott-section calls must send this.
  const orgAuthIdParam = useAppSelector((state) => state.orgDetails.data?.authId) || '';
  const elliottOrgAuthId = (orgId as string) || orgAuthIdParam;
  const orgDisplayNameManual = useAppSelector((state) => state.orgDetails.data?.name);
  const orgLogo = useAppSelector((state) => state.orgDetails.data?.orgLogo);
  const parentIdParamNew = useAppSelector((state) => state.orgDetails.data?.grpId) || '';
  const articleState = useAppSelector((state) => state.allArticle);
  const isPublished = Boolean(useAppSelector((state) => state.groupDetails.data?.goalPublished));
  // Co-design: one admin in the editor at a time; the rest get a read-only view.
  const editLockGroupId = state.groupId || '';
  useEditLockTarget(editLockGroupId, 'manual');
  const lockedOut = useEditLock().status === 'locked';
  // Existing group name from Redux — used as a fallback for the
  // Program name field when the admin entered this flow via the
  // Learning Framework dashboard's Continue link on a design-stage
  // program (the group was already created by the drawer, but the
  // creation context state.masterclass.title is empty).
  const existingGroupName = useAppSelector((state) => state.groupDetails.data?.name);
  // Stable when the org's article list hasn't loaded (state.allArticle.data is
  // undefined until some other surface fetches it, and this builder never does).
  // A `|| []` here minted a fresh array every render, so articleMap and
  // hydrateMaterials changed identity every render, so the hydration effect below
  // re-ran every render — and hydrateFromGoalLevels mints a new nanoid per MCQ
  // option on every call, so its JSON never matched and setModules fired forever.
  const articleList = articleState.data?.ArticleList || EMPTY_ARTICLE_LIST;
  const articleMap = useMemo(
    () =>
      articleList.reduce<Record<string, any>>((acc, article) => {
        acc[article.articleId] = article;
        return acc;
      }, {}),
    [articleList]
  );
  const hydrateMaterials = useCallback(
    (mods: ModuleDraft[]) =>
      mods.map((module) => ({
        ...module,
        lessons: module.lessons.map((lesson) => {
          const rawMaterials = lesson.materials && lesson.materials.length > 0 ? lesson.materials : [];
          const legacyArticles = (lesson as any).libraryArticles || [];
          const mergedMaterials: LessonMaterial[] = [
            ...rawMaterials,
            ...legacyArticles
              .filter((id: string) => !rawMaterials.some((mat) => (mat.articleId || mat.id) === id))
              .map((articleId: string) => ({
                id: articleId,
                articleId,
                title: '',
                reference: '',
                source: 'library' as const
              }))
          ];

          const hydrated = mergedMaterials.map((material) => {
            const articleId = material.articleId || material.id;
            const article = articleId ? articleMap[articleId] : null;
            const source = material.source || (article ? 'library' : 'custom');
            if (source !== 'library') return material;
            if (!article) {
              return {
                ...material,
                id: material.id || articleId || nanoid(),
                articleId: articleId || material.articleId || '',
                title: material.title || 'Resource',
                thumbnail: material.thumbnail,
                reference: material.reference || '',
                readTime: material.readTime
              };
            }
            return {
              ...material,
              id: material.id || article.articleId,
              articleId: article.articleId,
              title: material.title || article.articleName || 'Resource',
              thumbnail: material.thumbnail || article.coverImageUrl,
              readTime: material.readTime || article.readTime,
              reference: material.reference || article.articleUrl || article.articleLink || article.fileUrl || ''
            };
          });

          return { ...lesson, materials: hydrated };
        })
      })),
    [articleMap]
  );
  const hydrateFromGoalLevels = useCallback((): ModuleDraft[] => {
    const goalLevels: any[] = (state.masterclass as any)?.levels || [];
    if (!goalLevels.length) return [];
    return goalLevels.map((level, levelIdx) => {
      const lessons =
        level.tasks?.map((task: any) => {
          const feedbackQuestions: FeedbackQuestion[] =
            task.menteeQuestions
              ?.map((q: any) => ({
                id: q._id || nanoid(),
                _id: q._id,
                prompt: q.menteeQues || '',
                type: mapAnswerFormatToFeedbackType(q?.menteeAnsType || q?.type)
              }))
              .filter(Boolean) || [];

          const mcqs =
            task.MCQ?.map((mcq: any) => {
              const rawOptions: any[] = Array.isArray(mcq?.options) ? mcq.options : [];
              const options = rawOptions.map((opt: any, optionIdx: number) => {
                const optionText =
                  typeof opt === 'string'
                    ? opt
                    : opt?.optionText || opt?.label || opt?.value || `Option ${optionIdx + 1}`;
                return { id: nanoid(), label: optionText };
              });
              const decryptedCorrectAnswer =
                typeof mcq?.correctAns === 'string' && mcq.correctAns.trim()
                  ? getDecryptAns(mcq.correctAns)
                  : '';
              const normalizedDecryptedAnswer = String(decryptedCorrectAnswer || '').trim();
              const correctByCorrectAns = normalizedDecryptedAnswer
                ? options.find((opt: any) => String(opt.label || '').trim() === normalizedDecryptedAnswer)?.id
                : '';
              const ansFlagIndex = rawOptions.findIndex((opt: any) => Boolean(opt?.ans));
              const correctByAnsFlag = ansFlagIndex > -1 ? options[ansFlagIndex]?.id : '';
              const correctOptionId = correctByCorrectAns || correctByAnsFlag || options[0]?.id || '';
              return {
                id: mcq?._id || nanoid(),
                _id: mcq?._id,
                prompt: mcq?.ques || '',
                options,
                correctOptionId
              };
            }) || [];

          const materials =
            task.libraryArticles?.map((articleId: string) => ({
              id: articleId || nanoid(),
              articleId,
              title: '',
              reference: '',
              source: 'library' as const
            })) || [];

          return {
            id: task._id || nanoid(),
            title: task.taskName || `Lesson ${levelIdx + 1}`,
            description: task.taskDescription || '',
            materials,
            feedbackQuestions,
            multipleChoiceQuestions: mcqs
          } as Lesson;
        }) || [];

      return {
        id: level._id || nanoid(),
        title: level.levelName || `Module ${levelIdx + 1}`,
        objective: level.levelDescription || '',
        lessons: lessons.length ? lessons : [createLesson()]
      };
    });
  }, [state.masterclass]);
  const isAIMode = state.buildMode === 'ai';
  const isManualFlow = state.buildMode === 'manual';
  const navigate = useNavigate();

  const IMAGE_LIMIT = 5;
  const VIDEO_LIMIT = 3;
  const routerLocation = useLocation();
  const showManualFooter = !isAIMode && routerLocation.pathname.includes('/manual');
  const isMongoId = (val?: string) => Boolean(val && /^[a-fA-F0-9]{24}$/.test(val));
  const [infoVisible, setInfoVisible] = useState(true);
  const [materialsModalLesson, setMaterialsModalLesson] = useState<{ moduleId: string; lessonId: string } | null>(null);
  const [materialDetailsCache, setMaterialDetailsCache] = useState<Record<string, ArticleDetailsForMaterials | null>>(
    {}
  );
  const materialFetchPendingRef = useRef<Set<string>>(new Set());

  const articleListLength = articleList.length;

  const userRoleOverride = localStorage.getItem('role') || 'mentor';
  const groupIdForArticles = state.groupId || routeGroupId;
  const orgIdParamArticlesManual = orgId || '';
  const orgIdUsedForArticles = isManualFlow
    ? orgIdParamArticlesManual || orgIdParamNew
    : orgIdParamNew || orgIdParamArticlesManual;

  const isContentGenerating = Boolean(state.ai.contentLoading || state.ai.contentPollingActive);
  const contentProgressValue = state.ai.contentProgress?.percentage ?? null;
  const shouldDisablePendingActions = dirty && !saving && !(isAIMode && isContentGenerating);
  const isNextButtonDisabled = !state.groupId || saving || publishing || shouldDisablePendingActions || !hasSavedBeforeNext;
  const canSaveWithoutDirty = isAIMode && Boolean(state.ai.content) && !saving && !isContentGenerating;
  const canTriggerSave = isAIMode ? dirty || (canSaveWithoutDirty && !hasSavedBeforeNext) : dirty;
  // Create Program (embedded Add Program modal only) unlocks once there is saved
  // content and no pending edits — mirrors the AIMD Content screen's Create gate,
  // which can't fire while changes still need saving.
  const canCreateProgram = Boolean(hasSavedBeforeNext) && !dirty && !saving && modules.length > 0;
  useEffect(() => {}, [isAIMode, isContentGenerating, contentProgressValue]);
  useEffect(() => {
    if (!isAIMode) return;
    const content = state.ai.content;
    if (!content) return;
    if (lastContentRef.current !== content) {
      lastContentRef.current = content;
      if (hasSavedBeforeNext) {
        setHasSavedBeforeNextState(false);
      }
    }
  }, [hasSavedBeforeNext, isAIMode, setHasSavedBeforeNextState, state.ai.content]);
  const feedbackAnswerOptions = useMemo(
    () => [
      { label: 'Text', value: 'Open input field', type: 'text' as FeedbackQuestionType },
      { label: '1-5 Rating', value: '1-5 rating', type: 'rating' as FeedbackQuestionType },
      { label: 'Yes or No', value: 'Yes, No', type: 'yesno' as FeedbackQuestionType },
      { label: 'List of Industries', value: 'List of industries', type: 'list' as FeedbackQuestionType },
      { label: 'List of Roles', value: 'List of roles', type: 'roles' as FeedbackQuestionType }
    ],
    []
  );

  const activeModule = useMemo(
    () => modules.find((m) => m.id === selectedModuleId) || null,
    [modules, selectedModuleId]
  );
  const activeLesson = useMemo(
    () => activeModule?.lessons.find((l) => l.id === selectedLessonId) || null,
    [activeModule, selectedLessonId]
  );

  useEffect(() => {
    if (!activeLesson || isEditingLesson) return;
    setLessonDraft({ title: activeLesson.title, description: activeLesson.description });
    setIsEditingLesson(false);
  }, [activeLesson, isEditingLesson]);

  useEffect(() => {
    if (videoPollingJobs.length === 0) return;
    const generationId = state.generationId;
    if (!generationId) return;

    videoPollingRef.current = setInterval(async () => {
      const pending = videoPollingJobs.filter((j) => j.lessonId === activeLesson?.id);
      if (pending.length === 0) return;
      for (const job of pending) {
        try {
          const res = await API.getLessonVideoStatus({ programGenerationId: generationId, videoJobId: job.videoJobId });
          const { status, url } = res.data;
          if (status === 'completed' && url) {
            insertMediaRef.current?.insertVideo(url);
            setVideoPollingJobs((prev) => prev.filter((j) => j.videoJobId !== job.videoJobId));
            toast.success('Video added to the lesson.');
          } else if (status === 'failed') {
            setVideoPollingJobs((prev) => prev.filter((j) => j.videoJobId !== job.videoJobId));
            toast.error('Elliott could not make the video. Try again.');
          }
        } catch {
          // poll silently
        }
      }
    }, 5000);

    return () => { if (videoPollingRef.current) clearInterval(videoPollingRef.current); };
  }, [videoPollingJobs, activeLesson?.id, state.generationId]);

  const handleGenerateMedia = async () => {
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return;
    }
    const generationId = state.generationId;
    if (!generationId || !activeLesson || !mediaPrompt.trim()) return;
    setMediaGenerating(true);
    try {
      if (mediaDialog.type === 'image') {
        const res = await API.generateLessonImage({ programGenerationId: generationId, lessonId: activeLesson.id, prompt: mediaPrompt });
        const { url } = res.data;
        insertMediaRef.current?.insertImage(url);
        setImageCount((c) => c + 1);
        toast.success('Image added to the lesson.');
      } else {
        const res = await API.generateLessonVideo({ programGenerationId: generationId, lessonId: activeLesson.id, prompt: mediaPrompt });
        const { videoJobId } = res.data;
        setVideoPollingJobs((prev) => [...prev, { videoJobId, lessonId: activeLesson.id }]);
        setVideoCount((c) => c + 1);
        toast.info('Video requested. It will appear in the lesson in 1 to 5 minutes.');
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          (mediaDialog.type === 'image'
            ? 'Elliott could not make the image. Try again.'
            : 'Elliott could not make the video. Try again.')
      );
    } finally {
      setMediaGenerating(false);
      setMediaDialog({ type: 'image', open: false });
      setMediaPrompt('');
    }
  };

  const handleAddFeedbackQuestion = () => {
    // if (isPublished) return;
    setFeedbackDraft({ prompt: '', type: 'text' });
    setAddFeedbackModalOpen(true);
  };

  const handleSaveNewFeedback = () => {
    if (!activeModule || !activeLesson) return;
    if (!feedbackDraft.prompt.trim()) return;
    const newQuestion: FeedbackQuestion = {
      id: nanoid(),
      prompt: feedbackDraft.prompt.trim(),
      type: feedbackDraft.type
    };
    updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
      ...lesson,
      feedbackQuestions: [...lesson.feedbackQuestions, newQuestion]
    }));
    setAddFeedbackModalOpen(false);
  };

  const handleConfirmRemoveFeedback = () => {
    if (!activeModule || !activeLesson || !removeFeedbackId) return;
    updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
      ...lesson,
      feedbackQuestions: lesson.feedbackQuestions.filter((item) => item.id !== removeFeedbackId)
    }));
    setRemoveFeedbackId(null);
  };

  useEffect(() => {
    if (hydrationBlockedRef.current || (dirty && !isAIMode)) {
      return;
    }
    const sourceModules = (state.modules as ModuleDraft[] | undefined) ?? [];
    const hasDraftShape =
      sourceModules.length > 0 && sourceModules.every((m: any) => Array.isArray(m?.lessons) && m.lessons.length >= 0);
    const hydratedExisting = hasDraftShape ? hydrateMaterials(sourceModules) : [];
    const fallbackFromGoal = hydrateFromGoalLevels();
    const nextModules = hydratedExisting.length ? hydratedExisting : hydrateMaterials(fallbackFromGoal);
    const serializedCurrent = JSON.stringify(modules);
    const serializedNext = JSON.stringify(nextModules);
    if (serializedCurrent !== serializedNext) {
      setModules(nextModules);
    }

    setSelectedModuleId((prevModuleId) => {
      if (!nextModules.length) {
        setSelectedLessonId(null);
        return null;
      }

      if (!prevModuleId || !nextModules.some((module) => module.id === prevModuleId)) {
        const firstModule = nextModules[0];
        const firstLessonId = firstModule.lessons[0]?.id || null;
        setSelectedLessonId(firstLessonId);
        return firstModule.id;
      }

      const currentModule = nextModules.find((module) => module.id === prevModuleId);
      if (currentModule) {
        setSelectedLessonId((prevLessonId) => {
          if (!prevLessonId || !currentModule.lessons.some((lesson) => lesson.id === prevLessonId)) {
            return currentModule.lessons[0]?.id || null;
          }
          return prevLessonId;
        });
      }

      return prevModuleId;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.modules, state.masterclass, hydrateMaterials, hydrateFromGoalLevels, dirty]);

  useEffect(() => {
    if (
      lessonRenameMeta &&
      !modules.some((module) => module.lessons.some((lesson) => lesson.id === lessonRenameMeta.lessonId))
    ) {
      setLessonRenameMeta(null);
    }
  }, [modules, lessonRenameMeta]);

  useEffect(() => {
    const enriched = hydrateMaterials(modules);
    if (JSON.stringify(enriched) !== JSON.stringify(modules)) {
      setModules(enriched);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articleMap, hydrateMaterials]);

  const persistModules = useCallback(
    (updater: (prev: ModuleDraft[]) => ModuleDraft[]) => {
      setModules((prev) => {
        const next = updater(prev);
        dispatch({ type: 'SET_MODULES', payload: next });
        setDirtyState(true);
        return next;
      });
    },
    [dispatch, setDirtyState]
  );

  const syncModulesWithoutDirty = useCallback(
    (updater: (prev: ModuleDraft[]) => ModuleDraft[]) => {
      setModules((prev) => {
        const next = updater(prev);
        dispatch({ type: 'SET_MODULES', payload: next });
        return next;
      });
    },
    [dispatch]
  );

  const applyArticleDetails = useCallback(
    (articleId: string, article: ArticleDetailsForMaterials) => {
      if (!articleId || !article) return;
      const resolvedTitle = article.articleName || article.title || article.name || 'Resource';
      const resolvedThumbnail = article.coverImageUrl || article.coverImg || article.thumbnail || '';
      const resolvedReference = article.articleUrl || article.articleLink || article.fileUrl || '';
      const resolvedReadTime = article.readTime;

      syncModulesWithoutDirty((prev) =>
        prev.map((module) => ({
          ...module,
          lessons: module.lessons.map((lesson) => ({
            ...lesson,
            materials: lesson.materials.map((material) => {
              const materialArticleId = material.articleId || material.id;
              if (materialArticleId !== articleId) {
                return material;
              }
              const needsTitle = !material.title || material.title === 'Resource';
              return {
                ...material,
                title: needsTitle ? resolvedTitle : material.title,
                thumbnail: material.thumbnail || resolvedThumbnail,
                reference: material.reference || resolvedReference,
                readTime: material.readTime || resolvedReadTime
              };
            })
          }))
        }))
      );
    },
    [syncModulesWithoutDirty]
  );

  const fetchMissingMaterialDetails = useCallback(async () => {
    if (!orgIdUsedForArticles) return;
    const missingArticleIds: string[] = [];
    const targetLesson = materialsModalLesson
      ? modules.find((m) => m.id === materialsModalLesson.moduleId)?.lessons.find((l) => l.id === materialsModalLesson.lessonId)
      : activeLesson;
    const materials = targetLesson?.materials || [];
    materials.forEach((material) => {
      const materialSource = material.source || 'custom';
      if (materialSource !== 'library') return;
      const articleId = material.articleId || material.id;
      if (!articleId) return;
      const needsTitle = !material.title || material.title === 'Resource';
      const needsThumbnail = !material.thumbnail;
      const alreadyHasData = !needsTitle && !needsThumbnail;
      if (alreadyHasData) return;
      if (materialDetailsCache[articleId] !== undefined) return;
      if (articleMap[articleId]) return;
      missingArticleIds.push(articleId);
    });

    if (!missingArticleIds.length) return;

    const uniqueArticleIds = Array.from(new Set(missingArticleIds));
    const articleLocation = location || '';
    const groupIdForFetch = groupIdForArticles || '';
    for (const articleId of uniqueArticleIds) {
      if (materialFetchPendingRef.current.has(articleId)) continue;
      materialFetchPendingRef.current.add(articleId);
      try {
        const response = await API.getAllArticleById({
          orgId: orgIdUsedForArticles,
          articleId,
          groupId: groupIdForFetch,
          isContentSettings: true,
          articleLocation,
          useCache: false
        });
        const article = (response?.data?.article as ArticleDetailsForMaterials) || null;
        setMaterialDetailsCache((prev) => ({ ...prev, [articleId]: article }));
        if (article) {
          applyArticleDetails(articleId, article);
        }
      } catch (error) {
        console.error('Failed to fetch article details', error);
        setMaterialDetailsCache((prev) => ({ ...prev, [articleId]: null }));
      } finally {
        materialFetchPendingRef.current.delete(articleId);
      }
    }
  }, [
    activeLesson,
    materialDetailsCache,
    articleMap,
    orgIdUsedForArticles,
    groupIdForArticles,
    location,
    applyArticleDetails,
    materialsModalLesson,
    modules
  ]);

  useEffect(() => {
    fetchMissingMaterialDetails();
  }, [fetchMissingMaterialDetails]);

  useEffect(() => {
    if (!programName.trim() && state.masterclass?.title) {
      setProgramName(state.masterclass.title);
    } else if (hydrateFromUrl && !programName.trim() && existingGroupName) {
      // Learning Framework Continue path only — hydrate the field from
      // the BE group doc and mirror it into the creation context so the
      // rest of the flow stays in sync. Gated behind hydrateFromUrl so
      // we never race with DesignView's ManualBuilderInitializer, which
      // already pushes the template-driven title/modules into context.
      setProgramName(existingGroupName);
      dispatch({ type: 'UPDATE_MASTERCLASS', payload: { title: existingGroupName } });
    }
  }, [hydrateFromUrl, programName, state.masterclass?.title, existingGroupName, dispatch]);

  // Adopt the existing group from the URL so the save flow updates it
  // instead of creating a brand-new program. LL Continue path only; the
  // structure-editor mount (DesignView) gets its groupId from
  // ManualBuilderInitializer and must not run this fetch — it would
  // override the template-derived modules mid-render.
  useEffect(() => {
    if (!hydrateFromUrl) return;
    if (!routeGroupId || routeGroupId === 'admin') return;
    if (state.groupId === routeGroupId) return;
    dispatch({ type: 'SET_GROUP_ID', payload: routeGroupId });
    refreshGroupDetails(routeGroupId);
  }, [hydrateFromUrl, routeGroupId, state.groupId, dispatch, refreshGroupDetails]);

  const handleProgramNameChange = (value: string) => {
    setProgramName(value);
    setDirtyState(true);
    dispatch({ type: 'UPDATE_MASTERCLASS', payload: { title: value } });
    onProgramNameChange?.(value);
  };

  const handleAddModule = () => {
    persistModules((prev) => {
      const next = [...prev, createModule(prev.length + 1)];
      const newlyAdded = next[next.length - 1];
      setSelectedModuleId(newlyAdded.id);
      setSelectedLessonId(newlyAdded.lessons[0].id);
      return next;
    });
  };

  const handleAddLesson = (moduleId: string) => {
    persistModules((prev) =>
      prev.map((module) =>
        module.id === moduleId ? { ...module, lessons: [...module.lessons, createLesson()] } : module
      )
    );
  };

  const updateModule = (moduleId: string, updater: (module: ModuleDraft) => ModuleDraft) => {
    persistModules((prev) => prev.map((module) => (module.id === moduleId ? updater(module) : module)));
  };

  const updateLesson = (moduleId: string, lessonId: string, updater: (lesson: Lesson) => Lesson) => {
    updateModule(moduleId, (module) => ({
      ...module,
      lessons: module.lessons.map((lesson) => (lesson.id === lessonId ? updater(lesson) : lesson))
    }));
  };

  const removeModule = (moduleId: string) => {
    persistModules((prev) => {
      const filtered = prev.filter((module) => module.id !== moduleId);
      if (filtered.length === 0) {
        setSelectedModuleId(null);
        setSelectedLessonId(null);
      } else if (selectedModuleId === moduleId) {
        const nextModule = filtered[0];
        setSelectedModuleId(nextModule.id);
        setSelectedLessonId(nextModule.lessons[0]?.id || null);
      }
      return filtered;
    });
  };

  const removeLesson = (moduleId: string, lessonId: string) => {
    updateModule(moduleId, (module) => {
      const updatedLessons = module.lessons.filter((lesson) => lesson.id !== lessonId);
      if (updatedLessons.length === 0) {
        updatedLessons.push(createLesson());
      }
      if (module.id === selectedModuleId && lessonId === selectedLessonId) {
        setSelectedLessonId(updatedLessons[0].id);
      }
      return { ...module, lessons: updatedLessons };
    });
  };

  const buildLevelsPayload = useCallback(
    (sourceModules: ModuleDraft[]) =>
      sourceModules.map((module, moduleIndex) => {
        const modulePayload: Record<string, any> = {
          levelName: module.title || `Module ${moduleIndex + 1}`,
          levelDescription: module.objective || '',
          level: moduleIndex + 1,
          CheckInWithMentor: 'no',
          startDate: module.startDate || { type: 'activeNow', value: 0 },
          endDate: module.endDate || { type: 'activeNow', value: 0 }
        };

        if (module.sourceId) {
          modulePayload.id = module.sourceId;
        }

        modulePayload.tasks = module.lessons.map((lesson, lessonIndex) => {
          const lessonPayload: Record<string, any> = {
            taskName: lesson.title || `Lesson ${lessonIndex + 1}`,
            taskDescription: lesson.description || 'Lesson description',
            taskPosition: lessonIndex + 1,
            MCQ:
              lesson.multipleChoiceQuestions.length > 0
                ? lesson.multipleChoiceQuestions.map((mcq, mcqIdx) => {
                    const baseOptions = mcq.options.length > 0 ? mcq.options : [{ id: nanoid(), label: 'Option 1' }];
                    const paddedOptions =
                      baseOptions.length >= 4
                        ? baseOptions
                        : [
                            ...baseOptions,
                            ...Array.from({ length: 4 - baseOptions.length }).map((_, idx) => ({
                              id: nanoid(),
                              label: `Option ${baseOptions.length + idx + 1}`
                            }))
                          ];
                    const optionTexts = paddedOptions.map((opt, idx) => opt.label || `Option ${idx + 1}`);
                    const correctIndex =
                      mcq.correctOptionId && paddedOptions.findIndex((opt) => opt.id === mcq.correctOptionId) !== -1
                        ? paddedOptions.findIndex((opt) => opt.id === mcq.correctOptionId)
                        : 0;

                    const mcqPayload: Record<string, any> = {
                      ques: mcq.prompt || `MCQ ${mcqIdx + 1}`,
                      options: optionTexts.map((text, idx) => ({
                        optionText: text,
                        ans: idx === correctIndex
                      }))
                    };

                    if (mcq._id) {
                      mcqPayload._id = mcq._id;
                    }

                    return mcqPayload;
                  })
                : [],
            menteeQuestions:
              lesson.feedbackQuestions.length > 0
                ? lesson.feedbackQuestions.map((question, idx) => {
                    const questionPayload: Record<string, any> = {
                      menteeQues: question.prompt || `Question ${idx + 1}`,
                      menteeAnsType: mapFeedbackTypeToAnswerFormat(question.type)
                    };
                    if (question._id) {
                      questionPayload._id = question._id;
                    }
                    return questionPayload;
                  })
                : [],
            menteeAsmtQues: 'Have you completed this lesson?',
            menteeAsmtAnsType: 'Yes, No',
            menteeAsmtAnsOptions: ['Yes', 'No'],
            libraryArticles: lesson.materials
              .filter((material) => ['library', 'ai'].includes((material.source || 'custom') as string))
              .map((material) => material.articleId || material.id)
              .filter(Boolean),
            startDate: 0,
            endDate: 0
          };

          if (lesson.sourceId) {
            lessonPayload.id = lesson.sourceId;
          }

          return lessonPayload;
        });

        return modulePayload;
      }),
    []
  );

  const handleSave = async (): Promise<boolean> => {
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return false;
    }
    if (isEditingLesson && activeModule && activeLesson) {
      updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
        ...lesson,
        title: lessonDraft.title,
        description: lessonDraft.description
      }));
      setIsEditingLesson(false);
    }

    if (!programName.trim()) {
      toast.error('Program name is required');
      return false;
    }
    if (!modules.length) {
      toast.error('Add at least one module before saving');
      return false;
    }

    hydrationBlockedRef.current = true;
    setSaving(true);
    try {
      let orgForCreate = (orgIdParamNew as string) || '';
      const parentGroupId = (parentIdParamNew as string) || '';

      if (!orgForCreate || !parentGroupId) {
        toast.error('Missing program context');
        return false;
      }

      const safeGoalName = (programName.trim() || 'Program').replace(/[^a-zA-Z0-9\s-_]/g, '') || 'Program';
      const levels = buildLevelsPayload(modules) as any;
      dispatch({ type: 'UPDATE_MASTERCLASS', payload: { levels } });
      const updateGroupGoals = (goals: any) => {
        if (!goals) return;
        reduxDispatch(
          grpDetailsActions.updateGroupGoalInfo({
            programType: goals.programType || 'Custom',
            goalName: goals.goalName || safeGoalName,
            templateId: goals.templateId || '',
            programKPIS: goals.programKPIS || ''
          })
        );
      };

      const payload = { goalName: safeGoalName, programType: 'Custom', levels };
      const performTaskSave = (groupId: string) => {
        if (useUpdateGroupTasks && updateTemplateId) {
          return API.updateGroupTasks({
            data: payload,
            grpId: groupId,
            location,
            orgId: orgForCreate,
            templateId: updateTemplateId
          });
        }

        return API.saveGroupTasks({
          data: payload,
          grpId: groupId,
          location,
          orgId: orgForCreate
        });
      };

      const updateModelInfo = async (groupId: string) => {
        if (!model) return;
        const formData = new FormData();
        formData.append('model', model);
        formData.append('mentee', 'mentee');
        formData.append('mentor', 'mentor');
        if (programDueDate) {
          // JSON-stringified object for the BE Joi validator.
          formData.append(
            'programDueDate',
            JSON.stringify({
              existingMembers: {
                type: programDueDate.existingMembers.type,
                value: Number(programDueDate.existingMembers.value) || 0
              },
              newMembers: {
                type: programDueDate.newMembers.type,
                value: Number(programDueDate.newMembers.value) || 0
              }
            })
          );
        }

        await API.updateGroupInfo({
          orgId: orgForCreate,
          grpId: groupId,
          formData
        });
      };

      if (isAIMode) {
        if (!state.groupId) {
          toast.error('Missing workspace. Please start the build again.');
          return false;
        }

        const response = await performTaskSave(state.groupId);

        updateGroupGoals(response.data?.goals);
        await updateModelInfo(state.groupId);
        if (!useUpdateGroupTasks) {
          await API.updateGroupSettings({ groupId: state.groupId, location });
        }
        toast.success('Draft saved');
        // setDirtyState(false);
        return true;
      }

      let targetGroupId = state.groupId;

      if (!targetGroupId) {
        const createRes = await API.createGroup({
          orgId: orgForCreate,
          parent: parentGroupId,
          name: programName,
          location
        });

        targetGroupId =
          createRes?.data?.data?._id ||
          createRes?.data?.data?.grpId ||
          createRes?.data?.groupDetails?.grpId ||
          createRes?.data?.grpId ||
          createRes?.data?.groupId ||
          createRes?.data?.id;

        if (!targetGroupId) {
          toast.error('The program could not be built. Try again.');
          return false;
        }

        dispatch({ type: 'SET_GROUP_ID', payload: targetGroupId });

        // Program lands in the In Design column the moment it's created.
        eventTracking(events.programInDesign, {
          [attributeKeys.programName]: programName,
          [attributeKeys.adminProgramStatus]: 'In Design',
          [attributeKeys.programCreationFlow]: 'Manual',
          grpId: targetGroupId
        });
      }

      // [INSTRUMENTATION feat/program-design-blank-screen] log save attempt
      const __saveDebugId = `save-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      try {
        console.group(`[ProgramSave ${__saveDebugId}]`);
        console.log('grpId:', targetGroupId, 'orgId:', orgForCreate, 'templateId:', updateTemplateId);
        console.log('useUpdateGroupTasks:', useUpdateGroupTasks);
        console.log('payload meta:', {
          goalName: payload.goalName,
          moduleCount: payload.levels.length,
          totalLessons: payload.levels.reduce(
            (acc: number, l: any) => acc + (l?.tasks?.length || 0),
            0
          )
        });
      } catch {
        /* ignore */
      }

      const res = await performTaskSave(targetGroupId);

      // Honest response inspection — replaces the silent "Program saved" lie
      const status = (res as any)?.status;
      const dataPresent = Boolean((res as any)?.data);
      try {
        console.log(`[ProgramSave ${__saveDebugId}] response status:`, status, 'data present:', dataPresent);
        console.log(`[ProgramSave ${__saveDebugId}] response body:`, (res as any)?.data);
      } catch {
        /* ignore */
      }

      if (!res || (status && status >= 400) || !dataPresent) {
        // The axios response interceptor swallows certain 401s and returns undefined.
        // Without this check, the user sees a green success toast for a save that never happened.
        try {
          const buf = JSON.parse(localStorage.getItem('__dosen_debug_save') || '[]');
          buf.push({
            id: __saveDebugId,
            time: new Date().toISOString(),
            grpId: targetGroupId,
            templateId: updateTemplateId,
            status: status ?? 'no-response',
            outcome: 'silent-failure'
          });
          localStorage.setItem('__dosen_debug_save', JSON.stringify(buf.slice(-20)));
        } catch {
          /* ignore */
        }
        console.error(`[ProgramSave ${__saveDebugId}] silent failure — response missing or non-2xx`);
        console.groupEnd();
        toast.error(
          "Save didn't go through. Your session may have expired. Please refresh the page and try again. Your work is in the editor; don't close this tab."
        );
        return false;
      }
      console.groupEnd();
      toast.success('Program saved');

      if (!skipTopicIndustryUpdate) {
        await Promise.all([
          API.updateTopicsAndIndustry({
            grpId: targetGroupId,
            orgId: orgForCreate,
            data: { topics: ['Topic Creation'] },
            key: 'topics'
          }),
          API.updateTopicsAndIndustry({
            grpId: targetGroupId,
            orgId: orgForCreate,
            data: { industries: ['Industry Creation'] },
            key: 'industries'
          })
        ]);
      }

      updateGroupGoals(res.data?.goals);

      await updateModelInfo(targetGroupId);
      if (!useUpdateGroupTasks) {
        await API.updateGroupSettings({ groupId: targetGroupId, location });
      }

      // if (state.programType !== 'mentorship') {
      //   const publishResp = await API.publishTask({ location, grpId: targetGroupId, orgId: orgForCreate });
      //   if (publishResp?.status === 200) {
      //     reduxDispatch(grpDetailsActions.updatePublishKey(true));
      //     toast.success('Program published successfully');
      //   } else {
      //     toast.error('Program failed to publish');
      //   }
      // }

      setDirtyState(false);
      setHasSavedBeforeNextState(true);
      return true;
    } catch (error) {
      toast.error('The program could not be saved. Try again.');
      return false;
    } finally {
      setSaving(false);
      hydrationBlockedRef.current = false;
    }
  };

  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  const resolvePublishContext = () => {
    const resolvedOrgId = orgId || orgIdParamNew || '';
    return {
      orgId: resolvedOrgId,
      grpId: state.groupId || ''
    };
  };

  const handlePublishAndNext = async () => {
    if (editingFrozen) {
      toast.warn(EDITING_FROZEN_MESSAGE);
      return;
    }
    if (state.programType === 'mentorship') {
      navigate('members');
      return;
    }
    const { grpId, orgId } = resolvePublishContext();
    if (!grpId || !orgId) {
      toast.error('Missing program context. Please save your changes and try again.');
      return;
    }
    setPublishing(true);
    try {
      const publishResp = await API.publishTask({ location, grpId, orgId });
      if (publishResp?.status === 200) {
        reduxDispatch(grpDetailsActions.updatePublishKey(true));
        const programModelManual = state.programType === 'guided-masterclass' ? 'Guided Course' : 'Course';
        const creationFlow =
          state.buildMode === 'ai' ? 'AI MD' : updateTemplateId ? 'Template' : 'Manual';
        const publishedName = programName.trim() || state.masterclass?.title || 'Program';
        eventTracking(events.programPublished, {
          [attributeKeys.programName]: publishedName,
          [attributeKeys.programModel]: programModelManual,
          [attributeKeys.programCreationFlow]: creationFlow,
          [attributeKeys.adminProgramStatus]: 'Published',
          [attributeKeys.organizationName]: orgDisplayNameManual || '',
          grpId
        });
        // ATC: manual/template flows finish creation here — freeze the timer and
        // emit the per-creation row (deduped per program).
        await trackProgramCreated({
          grpId,
          programName: publishedName,
          programModel: programModelManual,
          creationFlow,
          organizationName: orgDisplayNameManual || ''
        });
        toast.success(`${publishedName} is published.`);
        // Embedded (Add Program modal): finish back to the framework instead of
        // routing to the Members sub-step.
        if (onFinish) {
          onFinish();
          return;
        }
        navigate('members');
      } else {
        toast.error('The program could not be published. Please try again.');
      }
    } catch (error) {
      console.error('Program publish error', error);
      toast.error('The program could not be published. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  useEffect(() => {
    if (!registerSaveHandler) return undefined;
    registerSaveHandler(() => handleSaveRef.current());
    return () => registerSaveHandler(null);
  }, [registerSaveHandler]);

  const openModuleMenu = (event: React.MouseEvent<HTMLButtonElement>, moduleId: string) => {
    event.stopPropagation();
    setModuleMenuAnchor(event.currentTarget);
    setModuleMenuId(moduleId);
  };

  const closeModuleMenu = () => {
    setModuleMenuAnchor(null);
    setModuleMenuId(null);
  };

  const openLessonMenu = (event: React.MouseEvent<HTMLElement>, meta: { moduleId: string; lessonId: string }) => {
    event.stopPropagation();
    setLessonMenuAnchor(event.currentTarget);
    setLessonMenuMeta(meta);
  };

  const closeLessonMenu = () => {
    setLessonMenuAnchor(null);
    setLessonMenuMeta(null);
  };

  const beginRenameLesson = (moduleId: string, lessonId: string, currentTitle: string) => {
    setLessonRenameMeta({ moduleId, lessonId, title: currentTitle || '' });
    closeLessonMenu();
  };

  const finishLessonRename = () => {
    if (!lessonRenameMeta) return;
    const { moduleId, lessonId, title } = lessonRenameMeta;
    updateLesson(moduleId, lessonId, (lesson) => ({
      ...lesson,
      title: title.trim() || lesson.title
    }));
    setLessonRenameMeta(null);
  };

  const cancelLessonRename = () => {
    setLessonRenameMeta(null);
  };

  const beginRenameModule = (moduleId: string) => {
    setEditingModuleId(moduleId);
    closeModuleMenu();
  };

  const handleModuleDragDrop = (targetId: string | null) => {
    if (!draggingModule) return;
    persistModules((prev) => {
      const fromIndex = prev.findIndex((m) => m.id === draggingModule);
      if (fromIndex === -1) return prev;
      const toIndex = targetId ? prev.findIndex((m) => m.id === targetId) : prev.length - 1;
      if (toIndex === -1 || toIndex === fromIndex) return prev;
      const reordered = [...prev];
      const [moved] = reordered.splice(fromIndex, 1);
      const insertIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
      console.log('[ManualProgramBuilder] module DnD', {
        fromIndex,
        toIndex,
        insertIndex,
        draggingModule,
        targetId
      });
      reordered.splice(insertIndex, 0, moved);
      return reordered;
    });
    setDraggingModule(null);
    setDragOverModuleId(null);
  };

  const handleLessonDrop = (moduleId: string, targetLessonId: string | null) => {
    if (!draggingLesson || draggingLesson.moduleId !== moduleId) return;
    updateModule(moduleId, (module) => {
      const lessons = [...module.lessons];
      const fromIndex = lessons.findIndex((lesson) => lesson.id === draggingLesson.lessonId);
      if (fromIndex === -1) return module;
      const toIndex = targetLessonId ? lessons.findIndex((lesson) => lesson.id === targetLessonId) : lessons.length;
      if (toIndex === -1 || fromIndex === toIndex) return module;
      const [moved] = lessons.splice(fromIndex, 1);
      const insertIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
      console.log('[ManualProgramBuilder] lesson DnD', {
        moduleId,
        fromIndex,
        toIndex,
        insertIndex,
        draggingLesson,
        targetLessonId
      });
      lessons.splice(insertIndex, 0, moved);
      return { ...module, lessons };
    });
    setDraggingLesson(null);
    setDragOverLessonMeta(null);
  };

  const handleModuleListDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!draggingModule && !draggingLesson) return;
    event.preventDefault();
    const container = moduleListRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const threshold = 80;
    const scrollStep = 6;
    if (event.clientY < rect.top + threshold) {
      container.scrollBy({ top: -scrollStep, behavior: 'smooth' });
    } else if (event.clientY > rect.bottom - threshold) {
      container.scrollBy({ top: scrollStep, behavior: 'smooth' });
    }
  };

  const currentMaterialsSelection = useMemo(() => {
    if (!materialsModalLesson) return [] as string[];
    const module = modules.find((m) => m.id === materialsModalLesson.moduleId);
    const lesson = module?.lessons.find((l) => l.id === materialsModalLesson.lessonId);
    if (!lesson) return [] as string[];
    return lesson.materials.map((material) => material.articleId).filter(Boolean) as string[];
  }, [materialsModalLesson, modules]);

  const fetchArticles = useCallback(
    (options?: { force?: boolean }) => {
      const targetGroupId = parentIdParamNew;
      const headerOrgId = coId;
      if (!headerOrgId || !targetGroupId) return;
      if (!options?.force && (articleState.loading || articleListLength > 0)) return;
      reduxDispatch(
        fetchAllArticles({
          orgId: orgId ?? '',
          groupId: groupIdForArticles,
          helpSettings: false,
          useCache: false,
          role: 'admin'
        })
      );
    },
    [
      groupIdForArticles,
      orgId,
      coId,
      orgIdUsedForArticles,
      articleState.loading,
      articleListLength,
      reduxDispatch,
      userRoleOverride
    ]
  );

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMaterialSelection = (articleIds: string[]) => {
    if (!materialsModalLesson) return;
    const selectedMaterials = articleIds
      .map((articleId) => {
        const article = articleList.find((item) => item.articleId === articleId);
        if (!article) return null;
        const reference = ((article as any).articleUrl ||
          (article as any).articleLink ||
          article.fileUrl ||
          '') as string;
        const title = (article as any).articleName || (article as any).title || (article as any).name || 'Resource';
        const thumbnail = (article as any).coverImageUrl || (article as any).coverImg || '';
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return {
          id: articleId,
          articleId,
          title,
          reference,
          thumbnail,
          readTime: article.readTime,
          source: 'library'
        } as LessonMaterial;
      })
      .filter(Boolean) as LessonMaterial[];

    updateLesson(materialsModalLesson.moduleId, materialsModalLesson.lessonId, (lesson) => ({
      ...lesson,
      materials: [
        ...lesson.materials.filter((material) => (material.source || 'custom') !== 'library'),
        ...selectedMaterials
      ]
    }));
    setMaterialsModalLesson(null);
  };

  const closeResourceChoice = () => setResourceChoiceLesson(null);

  const handleSelectExistingResource = () => {
    if (!resourceChoiceLesson) return;
    fetchArticles({ force: true });
    setMaterialsModalLesson(resourceChoiceLesson);
    closeResourceChoice();
  };

  const handleCreateNewResource = () => {
    if (!resourceChoiceLesson) return;
    setAddEditModalMeta({
      open: true,
      action: 'Add',
      moduleId: resourceChoiceLesson.moduleId,
      lessonId: resourceChoiceLesson.lessonId
    });
    closeResourceChoice();
  };

  const loadArticleDetails = async (articleId?: string) => {
    if (!articleId) return null;
    if (articleDetailsCache[articleId]) {
      return articleDetailsCache[articleId];
    }
    try {
      const response = await API.getAllArticleById({
        orgId: orgId ?? '',
        articleId,
        groupId: state.groupId ?? undefined,
        isContentSettings: true,
        articleLocation: location,
        useCache: false
      });
      const articleData = response?.data?.article;
      if (articleData) {
        setArticleDetailsCache((prev) => ({ ...prev, [articleId]: articleData }));
      }
      return articleData;
    } catch (error) {
      console.error('Failed to load article details', error);
      return null;
    }
  };

  const openMaterialEditor = async (material: LessonMaterial) => {
    if (!activeModule || !activeLesson) return;
    const articleId = material.articleId || material.id;
    let articleData = articleId ? articleMap[articleId] : undefined;
    if (!articleData && articleId) {
      articleData = await loadArticleDetails(articleId);
    }
    setAddEditModalMeta({
      open: true,
      action: 'Edit',
      articleId,
      moduleId: activeModule.id,
      lessonId: activeLesson.id,
      materialId: material.id,
      material,
      articleData
    });
  };

  const handleCloseAddEditModal = () => {
    setAddEditModalMeta((prev) => ({
      ...prev,
      open: false,
      articleId: undefined,
      materialId: undefined,
      material: undefined,
      articleData: undefined
    }));
  };

  const handleNewArticleCreated = (article: any) => {
    setAddEditModalMeta((prev) => ({
      ...prev,
      open: false,
      materialId: undefined,
      material: undefined,
      articleData: undefined
    }));
    const newArticle = Array.isArray(article) ? article[0] : article;
    const moduleId = addEditModalMeta.moduleId || activeModule?.id;
    const lessonId = addEditModalMeta.lessonId || activeLesson?.id;
    if (!newArticle || !moduleId || !lessonId) return;
    const articleId = newArticle.articleId || newArticle._id || nanoid();
    const material: LessonMaterial = {
      id: articleId,
      articleId: articleId,
      title: newArticle.articleName || newArticle.title || newArticle.name || 'Resource',
      reference: newArticle.articleUrl || newArticle.articleLink || newArticle.fileUrl || '',
      thumbnail: newArticle.coverImageUrl || newArticle.coverImg || '',
      readTime: newArticle.readTime,
      source: 'library'
    };
    if (addEditModalMeta.action === 'Edit' && addEditModalMeta.materialId) {
      updateLesson(moduleId, lessonId, (lesson) => ({
        ...lesson,
        materials: lesson.materials.map((item) =>
          item.id === addEditModalMeta.materialId
            ? {
                ...item,
                title: material.title,
                reference: material.reference,
                thumbnail: material.thumbnail,
                readTime: material.readTime,
                articleId: material.articleId,
                source: item.source || material.source
              }
            : item
        )
      }));
      return;
    }

    updateLesson(moduleId, lessonId, (lesson) => {
      const existing = lesson.materials.filter((item) => (item.articleId || item.id) !== material.articleId);
      return {
        ...lesson,
        materials: [...existing, material]
      };
    });
  };


  const removeMaterial = (moduleId: string, lessonId: string, materialId: string) => {
    updateLesson(moduleId, lessonId, (lesson) => ({
      ...lesson,
      materials: lesson.materials.filter((material) => material.id !== materialId)
    }));
  };

  const renderEmptyState = () => (
    <Box
      flex={1}
      minHeight={0}
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      gap={3}
      pt="20px"
      pb={8}
      px={4}
      sx={{ overflowY: 'auto' }}
    >
      <Typography sx={{ color: '#FDFDFD', fontSize: 30, fontWeight: 500 }}>Start from scratch.</Typography>
      <Typography sx={{ color: '#B6B6B6', fontSize: 14 }}>
        This is your blank canvas. You can build a program that’s fully custom-designed around your own structure,
        titles, and flow.
      </Typography>
      <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 600 }}>
        When you’re ready, add modules to start adding your content.
      </Typography>
    </Box>
  );

  const handleStartLessonEdit = () => {
    setLessonDraft({ title: activeLesson?.title || '', description: activeLesson?.description || '' });
    setIsEditingLesson(true);
  };

  useEffect(() => {
    if (isEditingLesson && activeLesson && !lessonDraft.description) {
      setLessonDraft((prev) => ({
        ...prev,
        description: activeLesson.description || ''
      }));
    }
  }, [isEditingLesson, activeLesson?.description, lessonDraft.description, activeLesson]);

  const renderLessonEditor = () => {
    if (!activeModule || !activeLesson) return renderEmptyState();

    const updateLessonField = (field: keyof Lesson, value: any) => {
      updateLesson(activeModule.id, activeLesson.id, (lesson) => ({ ...lesson, [field]: value }));
    };

    const handleAddMaterial = () => {
      if (!activeModule || !activeLesson) return;
      setResourceChoiceLesson({ moduleId: activeModule.id, lessonId: activeLesson.id });
    };

    const handleCancelLessonEdit = () => {
      setIsEditingLesson(false);
      setLessonDraft({ title: activeLesson.title, description: activeLesson.description });
    };

    const handleSaveLessonEdit = () => {
      if (!activeModule || !activeLesson) return;
      updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
        ...lesson,
        title: lessonDraft.title,
        description: lessonDraft.description
      }));
      setIsEditingLesson(false);
    };

    const handleAddMultipleChoice = () => {
      const opt1 = { id: nanoid(), label: '' };
      const opt2 = { id: nanoid(), label: '' };
      updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
        ...lesson,
        multipleChoiceQuestions: [
          ...lesson.multipleChoiceQuestions,
          {
            id: nanoid(),
            prompt: '',
            options: [opt1, opt2],
            correctOptionId: opt1.id
          }
        ]
      }));
    };

    const handleOptionDrop = (mcqId: string, targetId: string | null) => {
      if (!draggingOption) return;
      const draggedOptionId = draggingOption.optionId;
      updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
        ...lesson,
        multipleChoiceQuestions: lesson.multipleChoiceQuestions.map((item) => {
          if (item.id !== mcqId) return item;
          const options = [...item.options];
          const fromIndex = options.findIndex((opt) => opt.id === draggedOptionId);
          const toIndex = targetId ? options.findIndex((opt) => opt.id === targetId) : options.length - 1;
          if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return item;
          const [moved] = options.splice(fromIndex, 1);
          options.splice(toIndex, 0, moved);
          let nextCorrect = item.correctOptionId;
          if (nextCorrect && !options.find((opt) => opt.id === nextCorrect)) {
            nextCorrect = options[0]?.id || '';
          }
          return { ...item, options, correctOptionId: nextCorrect };
        })
      }));
      setDraggingOption(null);
    };

    // Elliott sections (published-program designer): panel edits persist
    // immediately through the task-scoped endpoint — sync local state without
    // marking the designer dirty (the template Save round-trips independently).
    const handleElliottChange = (next: Partial<ElliottSections>) => {
      syncModulesWithoutDirty((prev) =>
        prev.map((m) =>
          m.id !== activeModule.id
            ? m
            : {
                ...m,
                lessons: m.lessons.map((l) => (l.id === activeLesson.id ? { ...l, elliott: next } : l))
              }
        )
      );
    };

    // A quiz improve replaces task.MCQ on the BE (encrypted at rest) — refetch
    // the template and swap in the fresh questions for this lesson only, so
    // other unsaved local edits survive.
    const handleQuizImproved = async () => {
      if (!updateTemplateId || !state.groupId) return;
      const moduleSourceId = activeModule.sourceId;
      const lessonSourceId = activeLesson.sourceId;
      try {
        const res = await API.getTemplateByTemplateId({
          templateId: updateTemplateId,
          location: location || '',
          grpId: state.groupId,
          orgId: elliottOrgAuthId
        });
        const freshLesson = mapTemplateToModules(res?.data?.data)
          .find((m) => m.sourceId === moduleSourceId)
          ?.lessons.find((l) => l.sourceId === lessonSourceId);
        if (!freshLesson) return;
        syncModulesWithoutDirty((prev) =>
          prev.map((m) =>
            m.id !== activeModule.id
              ? m
              : {
                  ...m,
                  lessons: m.lessons.map((l) =>
                    l.id === activeLesson.id
                      ? {
                          ...l,
                          multipleChoiceQuestions: freshLesson.multipleChoiceQuestions,
                          elliott: freshLesson.elliott
                        }
                      : l
                  )
                }
          )
        );
      } catch {
        toast.error('Quiz was regenerated. Reload the page to see the new questions.');
      }
    };

    // Quiz "Improve with AI" — regenerates this lesson's question set through the
    // task-scoped Elliott endpoint (empty feedback = plain regenerate), then
    // refetches so the encrypted task.MCQ swap shows up in the accordion above.
    const handleQuizImproveSubmit = async (feedback: string) => {
      if (editingFrozen) {
        toast.warn(EDITING_FROZEN_MESSAGE);
        return;
      }
      setQuizImproveLoading(true);
      try {
        await API.improveElliottSectionOnTask({
          grpId: state.groupId as string,
          levelId: activeModule.sourceId as string,
          taskId: activeLesson.sourceId as string,
          orgId: elliottOrgAuthId,
          section: 'quiz',
          feedback
        });
        await handleQuizImproved();
        toast.success('Quiz improved');
        setQuizImproveOpen(false);
      } catch (err: any) {
        const message = err?.response?.data?.message;
        toast.error(message || 'Elliott could not improve the quiz. Try again.');
      } finally {
        setQuizImproveLoading(false);
      }
    };

    // Only published templates have real BE ids (locally-added drafts carry
    // nanoid placeholders that the task-scoped endpoint would reject).
    const isObjectId = (v?: string) => Boolean(v && /^[0-9a-fA-F]{24}$/.test(v));
    const showElliottPanel =
      useUpdateGroupTasks &&
      Boolean(updateTemplateId) &&
      Boolean(state.groupId) &&
      isObjectId(activeModule.sourceId) &&
      isObjectId(activeLesson.sourceId);

    const moduleIndex = modules.findIndex((m) => m.id === activeModule.id) + 1;
    const lessonIndex = activeModule.lessons.findIndex((l) => l.id === activeLesson.id) + 1;
    const lessonPending = Boolean(activeLesson.status && activeLesson.status !== 'completed');
    const aiMaterialPlaceholder = 'https://placehold.co/135x80?text=AI';

    if (lessonPending) {
      return (
        <Box
          flex={1}
          minHeight={0}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={2}
        >
          <Typography sx={{ color: '#B6B6B6' }}>We are still generating this lesson. Please hold on.</Typography>
          <LinearProgress sx={{ width: '60%', borderRadius: 6 }} />
        </Box>
      );
    }

    return (
      <Box flex={1} minHeight={0} sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Stack spacing={4} sx={{ flex: 1, minHeight: 0, p: 3, overflowY: 'auto' }}>
          {isAIMode && infoVisible && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                bgcolor: '#2A2A2A',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              <InfoOutlinedIcon sx={{ color: '#F79420' }} />
              <Box flex={1}>
                <Typography sx={{ color: '#B6B6B6', fontSize: 13 }}>
                  This program takes approximately 2.5 hours to complete in full. Learners may complete it all at once
                  or space it out over time.
                </Typography>
              </Box>
              <Button size="small" onClick={() => setInfoVisible(false)} sx={{ color: '#FDFDFD' }}>
                Dismiss
              </Button>
            </Box>
          )}

          <Box display="flex" flexDirection="row" gap="10px" alignItems="center">
            <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>Module {moduleIndex}</Typography>
            <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 600 }}>
              {activeModule.title || 'Untitled Module'}
            </Typography>
          </Box>

          <Box sx={{ mt: '20px !important' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 500 }}>Lesson name</Typography>
              {!isEditingLesson && (
                <IconButton size="small" onClick={handleStartLessonEdit}>
                  <EditOutlinedIcon sx={{ color: '#FDFDFD' }} />
                </IconButton>
              )}
            </Box>
            {isEditingLesson ? (
              <DosenInput
                value={lessonDraft.title}
                onChange={(e) => setLessonDraft((draft) => ({ ...draft, title: e.target.value }))}
                fullWidth
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: 24,
                    fontWeight: 600,
                    color: '#FDFDFD'
                  }
                }}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ color: '#FDFDFD', fontSize: 24, fontWeight: 500 }}>
                  {activeLesson.title || 'Untitled Lesson'}
                </Typography>
                {/* Co-design: collaborators + Invite Admin (In Design only). */}
                {editLockGroupId && !isPublished && (
                  <CollaboratorBar grpId={editLockGroupId} programName={programName || existingGroupName} />
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ mt: '30px !important' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 700 }}>Lesson Body</Typography>
              {activeLesson && !isManualFlow && (
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <Box
                    component="button"
                    disabled={videoCount >= VIDEO_LIMIT}
                    onClick={() => { setMediaDialog({ type: 'video', open: true }); setMediaPrompt(''); }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      background: 'transparent',
                      border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                      borderRadius: '6px', px: 1.5, py: 0.75, cursor: 'pointer',
                      color: 'var(--Text-text-primary, #FDFDFD)',
                      fontFamily: 'Inter', fontSize: 12, fontWeight: 500,
                      opacity: videoCount >= VIDEO_LIMIT ? 0.4 : 1,
                      '&:hover': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                    }}
                  >
                    <AutoAwesomeIcon sx={{ fontSize: 14, color: 'var(--Stroke-highlight, #F79420)' }} />
                    {`Generate Lesson Video${videoCount > 0 ? ` (${VIDEO_LIMIT - videoCount} left)` : ''}`}
                  </Box>
                  <Box sx={{
                    position: 'absolute', top: -8, right: 4,
                    background: '#F79420',
                    color: '#fff',
                    fontSize: 9, fontWeight: 700, fontFamily: 'Inter',
                    px: 0.6, py: 0.2,
                    borderRadius: '4px',
                    lineHeight: '14px',
                    pointerEvents: 'none',
                    letterSpacing: '0.3px'
                  }}>
                    BETA
                  </Box>
                </Box>
              )}
            </Box>
            {isEditingLesson ? (
              <LessonDescriptionEditor
                key={`lesson-desc-${activeLesson.id}-${isEditingLesson ? 'edit' : 'view'}`}
                value={lessonDraft.description || ''}
                onChange={(value) => setLessonDraft((draft) => ({ ...draft, description: value }))}
                insertMediaRef={insertMediaRef}
              />
            ) : (
              <>
                {activeLesson.description?.trim() ? (
                  <Box
                    sx={{
                      color: '#FDFDFD',
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: '21px',
                      '& ul': { paddingLeft: '20px', margin: '8px 0' },
                      '& ol': { paddingLeft: '20px', margin: '8px 0' },
                      '& ul li': { listStyleType: 'disc' },
                      '& ol li': { listStyleType: 'decimal' },
                      // Pasted/generated HTML carries inline colors & highlights —
                      // force one white ink so admin preview matches the learner view.
                      '& *:not(a)': { color: '#FDFDFD !important', backgroundColor: 'transparent !important' },
                      '& a, & a *': { color: '#F79420 !important', backgroundColor: 'transparent !important' }
                    }}
                    dangerouslySetInnerHTML={{
                      __html: activeLesson.description.includes('<')
                        ? activeLesson.description
                        : activeLesson.description.replace(/\n/g, '<br />')
                    }}
                  />
                ) : (
                  <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 400, lineHeight: '21px' }}>
                    No description yet.
                  </Typography>
                )}
              </>
            )}
          </Box>

          {isEditingLesson && (
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Box
                component="button"
                onClick={handleCancelLessonEdit}
                sx={{
                  background: '#1C1C1C',
                  border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                  borderRadius: '8px',
                  px: 3,
                  py: 1.5,
                  cursor: 'pointer',
                  color: 'var(--Text-text-primary, #FDFDFD)',
                  fontFamily: 'Inter',
                  fontSize: 14,
                  fontWeight: 500
                }}
              >
                Cancel
              </Box>
              <Box
                component="button"
                onClick={handleSaveLessonEdit}
                sx={{
                  background: CTA_GRADIENT,
                  border: 'none',
                  borderRadius: '8px',
                  px: 3,
                  py: 1.5,
                  cursor: 'pointer',
                  color: '#FDFDFD',
                  fontFamily: 'Inter',
                  fontSize: 14,
                  fontWeight: 600
                }}
              >
                Confirm
              </Box>
            </Box>
          )}

          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography sx={{ color: '#B6B6B6', fontSize: 12, fontWeight: 700 }}>Resources</Typography>
              <Box display="flex" gap={1}>
                <Tooltip
                  title="Save your changes before adding a new material."
                  disableHoverListener={!shouldDisablePendingActions}
                >
                  <span style={{ display: 'inline-flex' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleAddMaterial}
                      disabled={shouldDisablePendingActions || saving}
                    >
                      Add Resource
                      {/* {state.groupId} */}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            <Box display="inline-flex" flexWrap="wrap" alignItems="flex-start" gap={3}>
              {activeLesson.materials.map((material) => {
                const materialSource = material.source || 'custom';
                const showCard = materialSource === 'library' || materialSource === 'ai';
                const isArticle = materialSource === 'library';
                const isAI = materialSource === 'ai';
                return (
                  <Box
                    key={material.id}
                    onClick={() => {
                      if (showCard) {
                        openMaterialEditor(material);
                      }
                    }}
                    sx={{
                      width: 150,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      position: 'relative',
                      cursor: isArticle ? 'pointer' : 'default'
                    }}
                  >
                    {showCard ? (
                      <>
                        <Box
                          component="img"
                          src={material.thumbnail || orgLogo || dosenFavicon}
                          alt={material.title || 'Resource'}
                          onError={(e: any) => {
                            e.target.src = orgLogo || dosenFavicon;
                          }}
                          sx={{
                            width: '100%',
                            height: 90,
                            borderRadius: 1,
                            outline: '2px solid #FDFDFD',
                            objectFit: 'cover'
                          }}
                        />
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography sx={{ color: '#FDFDFD', fontSize: 12 }} noWrap>
                            {material.title || (isAI ? 'Material from Elliott' : 'Resource')}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeMaterial(activeModule.id, activeLesson.id, material.id);
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" style={{ color: 'white', cursor: 'pointer' }} />
                          </IconButton>
                        </Box>
                        {isAI ? (
                          <Typography sx={{ color: '#868686', fontSize: 10 }}>Built by Elliott</Typography>
                        ) : (
                          material.readTime && (
                            <Typography sx={{ color: '#868686', fontSize: 10 }}>{material.readTime}</Typography>
                          )
                        )}
                      </>
                    ) : (
                      <Stack spacing={1}>
                        <DosenInput
                          label="Resource title"
                          value={material.title}
                          onChange={(e) =>
                            updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                              ...lesson,
                              materials: lesson.materials.map((item) =>
                                item.id === material.id ? { ...item, title: e.target.value } : item
                              )
                            }))
                          }
                        />
                        <DosenInput
                          label="Link or reference"
                          value={material.reference}
                          onChange={(e) =>
                            updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                              ...lesson,
                              materials: lesson.materials.map((item) =>
                                item.id === material.id ? { ...item, reference: e.target.value } : item
                              )
                            }))
                          }
                        />
                        <Button
                          size="small"
                          color="error"
                          onClick={() => removeMaterial(activeModule.id, activeLesson.id, material.id)}
                        >
                          Remove
                        </Button>
                      </Stack>
                    )}
                  </Box>
                );
              })}

              {/* <Box
                role="button"
                tabIndex={0}
                onClick={() => setMaterialsModalLesson({ moduleId: activeModule.id, lessonId: activeLesson.id })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setMaterialsModalLesson({ moduleId: activeModule.id, lessonId: activeLesson.id });
                  }
                }}
                sx={{
                  width: 150,
                  height: 110,
                  borderRadius: 1,
                  border: '2px dashed #2A2A2A',
                  color: '#B6B6B6',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  gap: 1
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    bgcolor: '#FDFDFD'
                  }}
                />
                <Typography sx={{ fontSize: 12 }}>Add new resource</Typography>
              </Box> */}
            </Box>
          </Box>

          <Divider sx={{ borderColor: '#2A2A2A' }} />

          <GenericAccordion
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontSize={12} fontWeight={700} color="white">
                  Survey
                </Typography>
                <>
                  <Button
                    disableRipple
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddFeedbackQuestion();
                    }}
                    // disabled={isPublished}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '6px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      fontFamily: 'Inter, sans-serif',
                      color: '#F79420',
                      fontSize: 12,
                      fontWeight: 600,
                      px: 1,
                      py: 0.5,
                      // opacity: isPublished ? 0.5 : 1,
                      // '&:hover': {
                      //   background: 'rgba(247, 148, 32, 0.08)'
                      // }
                    }}
                  >
                    Add
                    <AddIcon sx={{ fontSize: 20, color: '#F79420' }} />
                  </Button>
                </>
              </Box>
            }
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px,1fr))', gap: 2.5 }}>
              {activeLesson.feedbackQuestions.length === 0 && (
                <Typography sx={{ color: '#868686', fontSize: 13, px: 1 }}>No Survey questions yet.</Typography>
              )}
              {activeLesson.feedbackQuestions.map((question) => (
                <Box
                  key={question.id}
                  sx={{
                    p: 2.5,
                    borderRadius: 1.5,
                    background: '#1C1C1C',
                    border: '1px solid #2A2A2A',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2
                  }}
                >
                  <Typography sx={{ color: '#FDFDFD', fontSize: 16, fontWeight: 700, lineHeight: '24px' }}>
                    {question.prompt || 'Untitled question'}
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ color: '#868686', fontSize: 12 }}>
                      {getFeedbackTypeLabel(question.type)}
                    </Typography>
                    <Button
                      color="error"
                      onClick={() => {
                        // if (!isPublished) setRemoveFeedbackId(question.id);
                        setRemoveFeedbackId(question.id);

                      }}
                      // disabled={isPublished}
                      sx={{ textDecoration: 'underline', fontSize: 12, fontWeight: 600 }}
                    >
                      Remove
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          </GenericAccordion>

          <GenericAccordion
            defaultExpanded
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography fontSize={12} fontWeight={700} color="white">
                  Quiz Questions
                </Typography>
                <>
                  <Button
                    disableRipple
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddMultipleChoice();
                    }}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '6px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      fontFamily: 'Inter, sans-serif',
                      color: '#F79420',
                      fontSize: 12,
                      fontWeight: 600,
                      px: 1,
                      py: 0.5,
                      '&:hover': {
                        background: 'rgba(247, 148, 32, 0.08)'
                      }
                    }}
                  >
                    Add
                    <AddIcon sx={{ fontSize: 20, color: '#F79420' }} />
                  </Button>
                  {showElliottPanel && (
                    <Button
                      disableRipple
                      onClick={(e) => {
                        e.stopPropagation();
                        setQuizImproveOpen(true);
                      }}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '6px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        fontFamily: 'Inter, sans-serif',
                        color: '#F79420',
                        fontSize: 12,
                        fontWeight: 600,
                        px: 1,
                        py: 0.5,
                        '&:hover': {
                          background: 'rgba(247, 148, 32, 0.08)'
                        }
                      }}
                    >
                      Improve with Elliott
                      <AutoAwesomeIcon sx={{ fontSize: 16, color: '#F79420' }} />
                    </Button>
                  )}
                </>
              </Box>
            }
          >
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px,1fr))', gap: 2 }}>
              {activeLesson.multipleChoiceQuestions.length === 0 && (
                <Typography sx={{ color: '#868686', fontSize: 13, px: 1 }}>
                  No quiz questions yet.
                </Typography>
              )}
              {activeLesson.multipleChoiceQuestions.map((mcq) => {
                const selectedOptionId = mcq.correctOptionId || mcq.options[0]?.id || '';
                console.log('mcq', mcq);
                return (
                  <Box
                    key={mcq.id}
                    sx={{
                      p: 2,
                      borderRadius: 1,
                      background: '#1C1C1C',
                      border: '1px solid #2A2A2A',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <DragIndicatorIcon sx={{ color: '#868686' }} />
                      <DosenInput
                        label="Question"
                        value={mcq.prompt}
                        onChange={(e) =>
                          updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                            ...lesson,
                            multipleChoiceQuestions: lesson.multipleChoiceQuestions.map((item) =>
                              item.id === mcq.id ? { ...item, prompt: e.target.value } : item
                            )
                          }))
                        }
                        fullWidth
                      />
                    </Box>
                    <Stack spacing={1}>
                      {mcq.options.map((option) => (
                        <Box
                          key={option.id}
                          display="flex"
                          alignItems="center"
                          gap={1}
                          draggable
                          onDragStart={() => setDraggingOption({ mcqId: mcq.id, optionId: option.id })}
                          onDragOver={(e) => draggingOption && e.preventDefault()}
                          onDrop={() => handleOptionDrop(mcq.id, option.id)}
                          sx={{ cursor: 'grab' }}
                        >
                          <DragIndicatorIcon sx={{ color: '#868686', fontSize: 18 }} />
                          <Radio
                            checked={selectedOptionId === option.id}
                            onChange={() =>
                              updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                                ...lesson,
                                multipleChoiceQuestions: lesson.multipleChoiceQuestions.map((item) =>
                                  item.id === mcq.id ? { ...item, correctOptionId: option.id } : item
                                )
                              }))
                            }
                            sx={{
                              color: '#868686',
                              '&.Mui-checked': { color: '#FDFDFD' }
                            }}
                          />
                          <DosenInput
                            label="Option"
                            value={option.label}
                            onChange={(e) =>
                              updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                                ...lesson,
                                multipleChoiceQuestions: lesson.multipleChoiceQuestions.map((item) =>
                                  item.id === mcq.id
                                    ? {
                                        ...item,
                                        options: item.options.map((opt) =>
                                          opt.id === option.id ? { ...opt, label: e.target.value } : opt
                                        )
                                      }
                                    : item
                                )
                              }))
                            }
                            fullWidth
                          />
                          <IconButton
                            onClick={() =>
                              updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                                ...lesson,
                                multipleChoiceQuestions: lesson.multipleChoiceQuestions.map((item) => {
                                  if (item.id !== mcq.id) return item;
                                  const filtered = item.options.filter((opt) => opt.id !== option.id);
                                  const newCorrect =
                                    item.correctOptionId === option.id ? filtered[0]?.id || '' : item.correctOptionId;
                                  return { ...item, options: filtered, correctOptionId: newCorrect };
                                })
                              }))
                            }
                            sx={{ color: '#FDFDFD', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <Box sx={{ width: 16, height: 2, backgroundColor: '#FDFDFD' }} />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <IconButton
                        size="small"
                        onClick={() =>
                          updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                            ...lesson,
                            multipleChoiceQuestions: lesson.multipleChoiceQuestions.map((item) =>
                              item.id === mcq.id
                                ? { ...item, options: [...item.options, { id: nanoid(), label: '' }] }
                                : item
                            )
                          }))
                        }
                        sx={{ color: '#FDFDFD' }}
                      >
                        <AddIcon />
                      </IconButton>
                      <Box
                        sx={{
                          color: '#FF0000',
                          fontSize: 12,
                          fontWeight: 600,
                          textDecoration: 'underline',
                          minWidth: 0,
                          borderRadius: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '5px',
                          cursor: 'pointer'
                        }}
                        onClick={() => setRemoveMcqId(mcq.id)}
                      >
                        Remove
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </GenericAccordion>

          {showElliottPanel && (
            <ElliottSectionsPanel
              key={activeLesson.id}
              grpId={state.groupId as string}
              orgId={elliottOrgAuthId}
              levelId={activeModule.sourceId as string}
              taskId={activeLesson.sourceId as string}
              elliott={activeLesson.elliott}
              onElliottChange={handleElliottChange}
            />
          )}

          {/* Quiz "Improve with AI" — opened from the Quiz Questions accordion header */}
          {showElliottPanel && (
            <ImproveWithAIModal
              variant="section"
              open={quizImproveOpen}
              isLoading={quizImproveLoading}
              onClose={() => {
                if (!quizImproveLoading) setQuizImproveOpen(false);
              }}
              onSubmit={handleQuizImproveSubmit}
            />
          )}
        </Stack>
      </Box>
    );
  };

  const renderModuleList = () => (
    <Box
      sx={{
        width: hideSaveButton ? 390 : 320,
        borderRight: '1px solid #2A2A2A',
        background: '#1C1C1C',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden'
      }}
      onDragOver={handleModuleListDragOver}
      onDrop={(e) => {
        if (draggingModule && !dragOverModuleId) {
          handleModuleDragDrop(null);
        }
        e.stopPropagation();
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 600, flex: 1 }}>Modules</Typography>
        <IconButton
          size="small"
          onClick={handleAddModule}
          // Brandy + pulsating while there are no modules yet, to draw the admin
          // to the first "add module" action. Stops once a module is created.
          sx={
            modules.length === 0
              ? {
                  animation: 'modulesAddPulse 1.6s ease-in-out infinite',
                  '@keyframes modulesAddPulse': {
                    '0%, 100%': { transform: 'scale(1)', filter: 'drop-shadow(0 0 0 rgba(247,148,32,0))' },
                    '50%': { transform: 'scale(1.18)', filter: 'drop-shadow(0 0 4px rgba(247,148,32,0.85))' }
                  }
                }
              : undefined
          }
        >
          <AddIcon sx={{ color: modules.length === 0 ? '#F79420' : '#FDFDFD' }} />
        </IconButton>
      </Box>
      {modules.length === 0 ? null : (
        <Box
          ref={moduleListRef}
          sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
          onDragOver={handleModuleListDragOver}
        >
          {modules.map((module) => {
            const moduleIndex = modules.findIndex((m) => m.id === module.id);
            const isActiveModule = module.id === selectedModuleId;
            const moduleProcessing = Boolean(module.status && module.status !== 'completed');
            console.log('[ManualProgramBuilder] module row', {
              moduleId: module.id,
              status: module.status,
              moduleProcessing,
              selectedModuleId
            });
            return (
              <Box key={module.id}>
                {draggingModule && dragOverModuleId === module.id && (
                  <Box sx={{ height: 8, mx: 2, borderTop: '1px solid #F79420' }} />
                )}
                <Box
                  draggable={!moduleProcessing}
                  onDragStart={() => !moduleProcessing && setDraggingModule(module.id)}
                  onDragOver={(e) => {
                    if (moduleProcessing) return;
                    e.preventDefault();
                    if (draggingModule) {
                      setDragOverModuleId(module.id);
                    }
                  }}
                  onDrop={(e) => {
                    if (!moduleProcessing) {
                      handleModuleDragDrop(module.id);
                    }
                    e.stopPropagation();
                  }}
                  onDragEnd={() => {
                    setDraggingModule(null);
                    setDragOverModuleId(null);
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.5,
                    cursor: moduleProcessing ? 'not-allowed' : 'grab',
                    bgcolor: isActiveModule ? '#151515' : 'transparent',
                    opacity: moduleProcessing ? 0.6 : 1
                  }}
                  onClick={() => {
                    if (moduleProcessing) return;
                    setSelectedModuleId(module.id);
                    setSelectedLessonId(module.lessons[0]?.id || null);
                  }}
                >
                  <DragIndicatorIcon sx={{ color: '#868686' }} />
                  <Box flex={1} sx={{ pr: 1 }}>
                    <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>{`Module ${moduleIndex + 1}`}</Typography>
                    {editingModuleId === module.id ? (
                      <DosenInput
                        value={module.title}
                        onClick={(e: any) => e.stopPropagation()}
                        onChange={(e) =>
                          updateModule(module.id, (m) => ({
                            ...m,
                            title: e.target.value
                          }))
                        }
                        onBlur={() => setEditingModuleId(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setEditingModuleId(null);
                          } else if (e.key === 'Escape') {
                            setEditingModuleId(null);
                          }
                        }}
                        // sx={{ ...inputSx, mt: 0.5 }}
                      />
                    ) : (
                      <Box display="flex" alignItems="center" gap={1}>
                        {moduleProcessing ? (
                          <Skeleton variant="text" width={120} sx={{ bgcolor: '#333' }} />
                        ) : (
                          <Typography
                            sx={{ color: isActiveModule ? '#F79420' : '#FDFDFD', fontSize: 16, fontWeight: 600 }}
                          >
                            {module.title || 'Untitled Module'}
                          </Typography>
                        )}
                        {moduleProcessing && <CircularProgress size={12} sx={{ color: '#F79420' }} />}
                      </Box>
                    )}
                  </Box>
                  {/* <IconButton
                    size="small"
                    disabled={moduleProcessing}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (moduleProcessing) return;
                      handleAddLesson(module.id);
                    }}
                  >
                    <AddIcon fontSize="small" sx={{ color: '#FDFDFD' }} />
                  </IconButton> */}
                  <IconButton size="small" onClick={(e) => openModuleMenu(e, module.id)}>
                    <MoreVertIcon fontSize="small" sx={{ color: '#FDFDFD' }} />
                  </IconButton>
                </Box>
                <Divider sx={{ borderColor: '#2A2A2A' }} />
                {module.lessons.map((lesson) => {
                  const lessonIndex = module.lessons.findIndex((l) => l.id === lesson.id);
                  const isActiveLesson = isActiveModule && lesson.id === selectedLessonId;
                  const lessonProcessing = Boolean(lesson.status && lesson.status !== 'completed');
                  console.log('[ManualProgramBuilder] lesson row', {
                    lessonId: lesson.id,
                    status: lesson.status,
                    lessonProcessing,
                    selectedLessonId
                  });
                  const isRenamingLesson = lessonRenameMeta?.lessonId === lesson.id;
                  const renameValue = lessonRenameMeta?.title ?? lesson.title;
                  return (
                    <Box key={lesson.id}>
                      {draggingLesson &&
                        dragOverLessonMeta?.moduleId === module.id &&
                        dragOverLessonMeta?.lessonId === lesson.id && (
                          <Box sx={{ height: 8, mx: 2, borderTop: '1px solid #F79420' }} />
                        )}
                      <Box
                        draggable={!lessonProcessing}
                        onDragStart={() =>
                          !lessonProcessing && setDraggingLesson({ moduleId: module.id, lessonId: lesson.id })
                        }
                        onDragOver={(e) => {
                          if (lessonProcessing) return;
                          e.preventDefault();
                          if (draggingLesson) {
                            setDragOverLessonMeta({ moduleId: module.id, lessonId: lesson.id });
                          }
                        }}
                        onDrop={() => !lessonProcessing && handleLessonDrop(module.id, lesson.id)}
                        onDragEnd={() => {
                          setDraggingLesson(null);
                          setDragOverLessonMeta(null);
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 2,
                          py: 1,
                          cursor: lessonProcessing ? 'not-allowed' : 'grab',
                          bgcolor: isActiveLesson ? '#151515' : 'transparent',
                          opacity: lessonProcessing ? 0.6 : 1
                        }}
                        onClick={() => {
                          if (lessonProcessing) return;
                          setSelectedModuleId(module.id);
                          setSelectedLessonId(lesson.id);
                        }}
                        onDoubleClick={(event) => {
                          if (lessonProcessing) return;
                          event.stopPropagation();
                          beginRenameLesson(module.id, lesson.id, lesson.title || '');
                        }}
                      >
                        <DragIndicatorIcon sx={{ color: '#868686' }} />
                        <Box width={20} textAlign="center">
                          <Typography sx={{ color: '#FDFDFD', fontSize: 12, fontWeight: 600 }}>
                            {lessonIndex + 1}
                          </Typography>
                        </Box>
                        <Box flex={1}>
                          {isRenamingLesson ? (
                            <DosenInput
                              value={renameValue}
                              onChange={(e) =>
                                setLessonRenameMeta((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        title: e.target.value
                                      }
                                    : prev
                                )
                              }
                              onBlur={finishLessonRename}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  finishLessonRename();
                                }
                                if (e.key === 'Escape') {
                                  cancelLessonRename();
                                }
                              }}
                              onClick={(event) => event.stopPropagation()}
                              fullWidth
                              autoFocus
                            />
                          ) : lessonProcessing ? (
                            <Skeleton variant="text" width="80%" sx={{ bgcolor: '#333' }} />
                          ) : (
                            <Typography
                              sx={{ color: isActiveLesson ? '#F79420' : '#FDFDFD', fontSize: 14, fontWeight: 600 }}
                            >
                              {lesson.title}
                            </Typography>
                          )}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openLessonMenu(e, { moduleId: module.id, lessonId: lesson.id });
                          }}
                        >
                          <MoreVertIcon fontSize="small" sx={{ color: '#FDFDFD' }} />
                        </IconButton>
                      </Box>
                    </Box>
                  );
                })}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    sx={{ color: '#868686', fontSize: 12, pl: 3 }}
                    onClick={() => handleAddLesson(module.id)}
                  >
                    Add Lesson
                  </Button>
                </Box>
                <Divider sx={{ borderColor: '#2A2A2A' }} />
              </Box>
            );
          })}
          {draggingModule && dragOverModuleId === null && (
            <Box sx={{ height: 8, mx: 2, borderTop: '1px solid #F79420' }} />
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <ReadOnlyWhenLocked>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          flex: 1,
          minHeight: 0,
          p: hideSaveButton ? 0 : 3,
          bgcolor: '#0F0F0F',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <EditingFrozenBanner frozen={editingFrozen} message={EDITING_FROZEN_MESSAGE} sx={{ mb: 2 }} />
        <ReadOnlyWhenFrozen frozen={editingFrozen}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            borderRadius: hideSaveButton ? 0 : 2,
            border: '1px solid #2A2A2A',
            bgcolor: '#1C1C1C',
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          {/* Co-design lock banner (Invite Admin sits in line with the lesson name). */}
          {lockedOut && (
            <Box sx={{ px: 3, pt: 2, pb: hideSaveButton ? 2 : 0 }}>
              <EditLockBanner />
            </Box>
          )}
          {!hideSaveButton && (
            <Box
              sx={{ px: 3, py: 2.5, borderBottom: '1px solid #2A2A2A', display: 'flex', alignItems: 'center', gap: 3 }}
            >
              <DosenInput
                label="Program name"
                value={programName}
                onChange={(e) => handleProgramNameChange(e.target.value)}
                inputSx={{ width: 320 }}
              />
              {isAIMode && isContentGenerating ? (
                <Box flex={1} display="flex" flexDirection="column" gap={0.5}>
                  {/* <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>Generating AI content…</Typography> */}
                  <LinearProgress
                    variant={contentProgressValue ? 'determinate' : 'indeterminate'}
                    value={contentProgressValue ?? undefined}
                  />
                </Box>
              ) : (
                <Box flex={1} />
              )}
              {!hideSaveButton && showLessonEditor && (
                <Box display="flex" alignItems="center" gap={1.5} justifyContent="flex-end">
                  {state.groupId && (
                    <Tooltip
                    title={
                      programDueDate &&
                      (programDueDate.existingMembers.type !== 'activeNow' || programDueDate.newMembers.type !== 'activeNow')
                        ? 'Edit Program Due Date'
                        : 'Add Program Due Date'
                    }
                  >
                      <IconButton onClick={() => setProgramDueDateOpen(true)} size="small" sx={{ color: '#FDFDFD' }}>
                        <CalendarMonthIcon sx={{ fontSize: 28 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={editingFrozen ? EDITING_FROZEN_MESSAGE : ''}>
                    <span>
                      <DosenButton
                        disabled={editingFrozen || saving || (isAIMode && isContentGenerating) || !canTriggerSave}
                        onClick={handleSave}
                        sx={{
                          background:
                            canTriggerSave && !editingFrozen
                              ? 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)'
                              : 'rgba(0,0,0,0.2)',
                          color: canTriggerSave && !editingFrozen ? '#FDFDFD' : '#565656'
                        }}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </DosenButton>
                    </span>
                  </Tooltip>
                  {/* Embedded Add Program modal: Create Program finishes the flow
                      (publish → onFinish → back to framework). Locked until there
                      is saved content with no pending edits. */}
                  {onFinish && (
                    <DosenButton
                      disabled={publishing || !canCreateProgram}
                      onClick={handlePublishAndNext}
                      sx={{
                        background: canCreateProgram
                          ? 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)'
                          : 'rgba(0,0,0,0.2)',
                        color: canCreateProgram ? '#FDFDFD' : '#565656'
                      }}
                      >
                        {publishing ? 'Building...' : 'Build Program'}
                    </DosenButton>
                  )}
                </Box>
              )}
            </Box>
          )}
          <Box sx={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden', height: '100%' }}>
            {renderModuleList()}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                background: '#151515',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                overflow: 'hidden'
              }}
            >
              {showLessonEditor ? (
                renderLessonEditor()
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    px: '25px',
                    py: '100px',
                    overflow: 'hidden',
                    display: 'inline-flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    fontFamily: 'Inter !important'
                  }}
                >
                  <Box
                    sx={{
                      alignSelf: 'stretch',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                      gap: '30px',
                      fontFamily: 'Inter !important'
                    }}
                  >
                    {/* Título */}
                    <Typography
                      sx={{
                        alignSelf: 'stretch',
                        color: 'var(--Text-text-primary, #FDFDFD)',
                        fontSize: '30px',
                        fontFamily: 'Inter !important',
                        fontWeight: 500,
                        wordWrap: 'break-word'
                      }}
                    >
                      Review your program structure.
                    </Typography>

                    {/* Texto descritivo */}
                    <Typography
                      sx={{
                        color: 'var(--Text-text-secondary, #B6B6B6)',
                        fontSize: '14px',
                        fontFamily: 'Inter !important',
                        fontWeight: 400,
                        lineHeight: '19.88px',
                        whiteSpace: 'pre-line',
                        wordWrap: 'break-word'
                      }}
                    >
                      {`Over to the left, you’ll see our recommended Module and Lesson titles.
Before we go ahead and design the content for each one, take a moment to review these titles.

You can edit, reorder, delete, and add until you are satisfied with each.`}
                    </Typography>

                    {/* Texto final */}
                    <Typography
                      sx={{
                        alignSelf: 'stretch',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        color: 'var(--Text-text-primary, #FDFDFD)',
                        fontSize: '16px',
                        fontFamily: 'Inter !important',
                        fontWeight: 600,
                        lineHeight: '20.8px',
                        wordWrap: 'break-word'
                      }}
                    >
                      Once you’re happy with these, click on “Next” to review your contents.
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Menu anchorEl={moduleMenuAnchor} open={Boolean(moduleMenuAnchor)} onClose={closeModuleMenu}>
          <MenuItem
            onClick={() => {
              if (moduleMenuId) beginRenameModule(moduleMenuId);
            }}
          >
            Rename module
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (moduleMenuId) {
                setModuleDueDateTargetId(moduleMenuId);
                closeModuleMenu();
              }
            }}
          >
            Add Module Due Date
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (moduleMenuId) removeModule(moduleMenuId);
              closeModuleMenu();
            }}
          >
            Delete module
          </MenuItem>
        </Menu>

        {(() => {
          const selectedLesson =
            lessonMenuMeta &&
            modules
              .find((module) => module.id === lessonMenuMeta.moduleId)
              ?.lessons.find((lesson) => lesson.id === lessonMenuMeta.lessonId);
          const isLessonLocked = Boolean(selectedLesson?.status && selectedLesson.status !== 'completed');

          // Rename lesson mounts the name field (autoFocus) while this menu is still trapping focus:
          // the trap pulls focus back, the field blurs and its onBlur ends the rename at once. Closing
          // would also hand focus back to the ⋮ button. So the menu neither enforces nor restores focus.
          return (
            <Menu
              anchorEl={lessonMenuAnchor}
              open={Boolean(lessonMenuAnchor)}
              onClose={closeLessonMenu}
              disableEnforceFocus
              disableRestoreFocus
            >
              <MenuItem
                onClick={() => {
                  if (lessonMenuMeta) {
                    const currentModule = modules.find((module) => module.id === lessonMenuMeta.moduleId);
                    const currentLesson = currentModule?.lessons.find(
                      (lesson) => lesson.id === lessonMenuMeta.lessonId
                    );
                    beginRenameLesson(lessonMenuMeta.moduleId, lessonMenuMeta.lessonId, currentLesson?.title || '');
                  }
                }}
              >
                Rename lesson
              </MenuItem>
              <MenuItem
                onClick={() => {
                  if (lessonMenuMeta) removeLesson(lessonMenuMeta.moduleId, lessonMenuMeta.lessonId);
                  closeLessonMenu();
                }}
                disabled={isLessonLocked}
              >
                Delete lesson
              </MenuItem>
            </Menu>
          );
        })()}

        {showManualFooter && (
          <Box
            sx={{
              mt: 2,
              borderTop: '1px solid #2A2A2A',
              pt: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Button variant="outlined" onClick={() => navigate(-1)} sx={secondaryButtonSx}>
                Previous
            </Button>
            <Box display="flex" alignItems="center" gap={2}>
                {!state.groupId && (
                  <Typography sx={{ color: '#B6B6B6', fontSize: 12 }}>Save your content to unlock People.</Typography>
                )}
                <Tooltip title="Save your changes before continuing." disableHoverListener={!isNextButtonDisabled}>
                <span style={{ display: 'inline-flex' }}>
                  <Button
                    variant="contained"
                    onClick={handlePublishAndNext}
                    disabled={isNextButtonDisabled}
                    sx={ctaButtonSx}
                  >
                    {publishing ? 'Publishing...' : 'Next'}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        )}
        </ReadOnlyWhenFrozen>
      </Box>

      {/* Module Due Date Modal */}
      <DueDateModal
        open={Boolean(moduleDueDateTargetId)}
        onClose={() => setModuleDueDateTargetId(null)}
        mode="module"
        initialData={
          moduleDueDateTargetId
            ? (() => {
                const m = modules.find((mod) => mod.id === moduleDueDateTargetId);
                return m ? { startDate: m.startDate, endDate: m.endDate, description: m.objective } : undefined;
              })()
            : undefined
        }
        onSave={(data) => {
          if (!moduleDueDateTargetId) return;
          updateModule(moduleDueDateTargetId, (m) => ({
            ...m,
            startDate: data.startDate,
            endDate: data.endDate
          }));
          setModuleDueDateTargetId(null);
        }}
      />

      {/* Program Due Date Modal */}
      <DueDateModal
        open={programDueDateOpen}
        onClose={() => setProgramDueDateOpen(false)}
        mode="program"
        initialProgramData={programDueDate || undefined}
        onSave={() => {}}
        onSaveProgram={(data) => {
          setProgramDueDate(data);
          setProgramDueDateOpen(false);
        }}
      />

      <ContentsModal
        openContents={Boolean(materialsModalLesson)}
        onClose={() => setMaterialsModalLesson(null)}
        setArticles={handleMaterialSelection}
        articlesSelected={currentMaterialsSelection}
      />
      <Dialog
        open={Boolean(resourceChoiceLesson)}
        onClose={closeResourceChoice}
        fullWidth
        PaperProps={{
          sx: {
            width: 600,
            maxWidth: '90%',
            height: 254,
            bgcolor: 'rgba(67, 67, 76, 0.20)',
            color: '#FDFDFD',
            borderRadius: 2
          }
        }}
      >
        <CommonModalHeader title="Add new resource" handleCloseModal={closeResourceChoice} />
        <DialogContent sx={{ pt: 3, pb: 4 }}>
          <Typography
            align="center"
            color="#FDFDFD"
            fontSize={16}
            fontFamily="Inter, sans-serif"
            fontWeight={400}
            lineHeight="24px"
            sx={{ mb: 4 }}
          >
            How you want to add new resource?
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" gap={2} flexWrap="wrap">
            <Button
              onClick={handleSelectExistingResource}
              sx={{
                ...(secondaryButtonSx as object),
                width: 232,
                height: 49,
                borderRadius: '10px',
                fontSize: 16,
                fontWeight: 600,
                lineHeight: '24px'
              }}
            >
              Select Existing Resource
            </Button>
            <Button
              onClick={handleCreateNewResource}
              sx={{
                ...(ctaButtonSx as object),
                width: 232,
                height: 49,
                borderRadius: '10px',
                fontSize: 16,
                fontWeight: 600,
                lineHeight: '24px'
              }}
            >
              Add
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={addFeedbackModalOpen}
        onClose={() => setAddFeedbackModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0F0F0F',
            borderRadius: 2,
            border: '1px solid #444444',
            overflow: 'hidden',
            maxWidth: 550
          }
        }}
      >
        <Box
          sx={{
            width: '100%',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #2A2A2A',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, flex: 1 }}>
            Add a Survey question
          </Typography>
          <IconButton onClick={() => setAddFeedbackModalOpen(false)} sx={{ color: '#FDFDFD' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5
          }}
        >
          <DosenInput
            label="Question*"
            value={feedbackDraft.prompt}
            onChange={(e: any) => setFeedbackDraft((draft) => ({ ...draft, prompt: e.target.value }))}
            placeholder="Type here"
          />
          <Box>
            <InputLabel sx={{ color: '#FDFDFD', mb: 0.5 }}>Answer Type*</InputLabel>
            <DosenAutocomplete
              options={feedbackAnswerOptions}
              getOptionLabel={(opt: any) => opt.label}
              value={feedbackAnswerOptions.find((opt) => opt.type === feedbackDraft.type) || null}
              onChange={(_: any, opt: any) =>
                setFeedbackDraft((draft) => ({
                  ...draft,
                  type: opt?.type || 'text'
                }))
              }
              // renderInput={(params: any) => (
              //   <TextField
              //     {...params}
              //     fullWidth
              //     placeholder="Answer Format"
              //     sx={{
              //       '& .MuiInputBase-root': {
              //         background: 'rgba(0,0,0,0.70)',
              //         color: '#FDFDFD',
              //         borderRadius: '6px',
              //         outline: '1px solid #868686',
              //         '&:hover': { outlineColor: '#B4B4B4' },
              //         '&.Mui-focused': { outlineColor: '#F79420' },
              //         '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
              //       },
              //       '& input': {
              //         color: '#FDFDFD'
              //       }
              //     }}
              //   />
              // )}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <DosenButton tone="basic" onClick={() => setAddFeedbackModalOpen(false)} sx={{ flex: 1 }}>
            Cancel
          </DosenButton>
          <DosenButton onClick={handleSaveNewFeedback} sx={{ flex: 1 }} disabled={!feedbackDraft.prompt.trim()}>
            Add Question
          </DosenButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(removeFeedbackId)}
        onClose={() => setRemoveFeedbackId(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0F0F0F',
            borderRadius: 2,
            border: '1px solid #444444',
            overflow: 'hidden',
            maxWidth: 550
          }
        }}
      >
        <Box
          sx={{
            width: '100%',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #2A2A2A',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, flex: 1 }}>Remove Question</Typography>
          <IconButton onClick={() => setRemoveFeedbackId(null)} sx={{ color: '#FDFDFD' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 500 }}>
            Are you sure to remove this question?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <DosenButton tone="basic" onClick={() => setRemoveFeedbackId(null)} sx={{ flex: 1 }}>
            Cancel
          </DosenButton>
          <DosenButton onClick={handleConfirmRemoveFeedback} sx={{ flex: 1 }}>
            Remove
          </DosenButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(removeMcqId)}
        onClose={() => setRemoveMcqId(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0F0F0F',
            borderRadius: 2,
            border: '1px solid #444444',
            overflow: 'hidden',
            maxWidth: 550
          }
        }}
      >
        <Box
          sx={{
            width: '100%',
            px: 3,
            py: 2.5,
            borderBottom: '1px solid #2A2A2A',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, flex: 1 }}>Remove Assessment</Typography>
          <IconButton onClick={() => setRemoveMcqId(null)} sx={{ color: '#FDFDFD' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            px: 3,
            py: 2.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5,
            alignItems: 'center',
            textAlign: 'center'
          }}
        >
          <Typography sx={{ color: '#B6B6B6', fontSize: 14, fontWeight: 500 }}>
            Are you sure to remove this assessment?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <DosenButton tone="basic" onClick={() => setRemoveMcqId(null)} sx={{ flex: 1 }}>
            Cancel
          </DosenButton>
          <DosenButton
            onClick={() => {
              if (!removeMcqId || !activeModule || !activeLesson) return;
              updateLesson(activeModule.id, activeLesson.id, (lesson) => ({
                ...lesson,
                multipleChoiceQuestions: lesson.multipleChoiceQuestions.filter((item) => item.id !== removeMcqId)
              }));
              setRemoveMcqId(null);
            }}
            sx={{ flex: 1 }}
          >
            Remove
          </DosenButton>
        </DialogActions>
      </Dialog>
      <AddEditContent
        openForm={addEditModalMeta.open}
        handleClickCloseForm={handleCloseAddEditModal}
        setOpenForm={(value: boolean) => setAddEditModalMeta((prev) => ({ ...prev, open: value }))}
        selectedActiveId={addEditModalMeta.articleId}
        singleListData={
          addEditModalMeta.articleData ||
          addEditModalMeta.material ||
          (addEditModalMeta.articleId ? articleMap[addEditModalMeta.articleId] : undefined)
        }
        action={addEditModalMeta.action}
        setAction={undefined}
        onNewArticleCreated={handleNewArticleCreated}
        newGroupId={state.groupId ?? ''}
      />

      <Dialog
        open={mediaDialog.open}
        onClose={() => !mediaGenerating && setMediaDialog({ type: 'image', open: false })}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#0F0F0F', color: '#FDFDFD', borderRadius: 2, border: '1px solid #444444', maxWidth: 550 }
        }}
      >
        <CommonModalHeader
          title={mediaDialog.type === 'image' ? 'Generate image' : 'Generate video'}
          handleCloseModal={() => !mediaGenerating && setMediaDialog({ type: 'image', open: false })}
        />
        <DialogContent sx={{ px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography sx={{ color: '#B6B6B6', fontSize: 13 }}>
            {mediaDialog.type === 'image'
              ? 'Describe the image you want to generate for this lesson.'
              : 'Describe what this training video should cover. A presenter will deliver the content.'}
          </Typography>
          <DosenInput
            label={mediaDialog.type === 'image' ? 'Image description' : 'Video instructions'}
            value={mediaPrompt}
            onChange={(e: any) => setMediaPrompt(e.target.value)}
            placeholder={
              mediaDialog.type === 'image'
                ? 'e.g. An infographic showing the 5 steps of onboarding'
                : 'e.g. Explain the key principles of effective communication in the workplace'
            }
            multiline
            rows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <DosenButton tone="basic" onClick={() => setMediaDialog({ type: 'image', open: false })} disabled={mediaGenerating} sx={{ flex: 1 }}>
            Cancel
          </DosenButton>
          <DosenButton onClick={handleGenerateMedia} disabled={!mediaPrompt.trim() || mediaGenerating} sx={{ flex: 1 }}>
            {mediaGenerating ? 'Generating...' : 'Generate'}
          </DosenButton>
        </DialogActions>
      </Dialog>
    </ReadOnlyWhenLocked>
  );
};

// While another co-design admin held the edit lock this admin saw a read-only
// snapshot. When it comes back, re-hydrate the builder from the BE buildState
// (their saved work) and remount, so nothing stale is edited or saved over it.
// The lock provider sits above, so the remount keeps the lock.
const ManualBuilderReloadOnRegain = (props: ManualProgramBuilderProps) => {
  const { dispatch } = useProgramCreation();
  const { regainedCount, grpId } = useEditLock();
  const [generation, setGeneration] = useState(0);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    if (!regainedCount || !grpId) return undefined;
    let cancelled = false;
    setReloading(true);
    // Close the write gate until the reload lands, so the context's debounced
    // buildState save can't push the stale snapshot over the other admin's work.
    setBuildStateHydrating(grpId, true);
    API.getBuildState({ grpId })
      .then((resp: any) => {
        const remote = resp?.data ?? null;
        if (!cancelled && remote && typeof remote === 'object' && remote.programType) {
          dispatch({ type: 'HYDRATE_FROM_BUILD_STATE', payload: remote });
        }
      })
      .catch((err: unknown) => console.error('[ManualProgramBuilder] reload after lock regained failed', err))
      .finally(() => {
        setBuildStateHydrating(grpId, false);
        if (cancelled) return;
        setReloading(false);
        setGeneration((g) => g + 1);
        toast.info("You're in the editor now, showing the latest saved changes.");
      });
    return () => {
      cancelled = true;
    };
  }, [regainedCount]);

  if (reloading) {
    return (
      <Box aria-busy="true" sx={{ flex: 1, minHeight: 320, display: 'flex', gap: 3, p: 3, bgcolor: '#0F0F0F' }}>
        <Box sx={{ width: 280, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <Bone key={i} height={40} />
          ))}
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Bone width="40%" height={28} />
          <Bone height={220} />
          <Bone width="60%" height={20} />
          <Bone height={90} />
        </Box>
      </Box>
    );
  }
  return <ManualProgramBuilderInner key={generation} {...props} />;
};

export const ManualProgramBuilder = (props: ManualProgramBuilderProps) => {
  // Known up front, so the provider starts as 'acquiring' and the context's
  // buildState save waits for the lock answer instead of writing first.
  const { state } = useProgramCreation();
  return (
    <ProgramEditLockProvider grpId={state.groupId || ''}>
      <ManualBuilderReloadOnRegain {...props} />
    </ProgramEditLockProvider>
  );
};
